import {
  addMonths,
  addQuarters,
  addWeeks,
  endOfDay,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subSeconds,
  subWeeks,
  subYears,
} from 'date-fns';

import {
  Advice,
  BulletList,
  Continue,
  ManagerRabbit,
  Play,
  Praise,
  Public,
  ReceiverManager,
} from 'assets/assetsV1/iconComponents';
import { Person } from 'assets/assetsV1/iconComponents/Person';

import { getAdditionalReviewerTerm, getReviewerTerm } from 'utils/helpers/nomenclatureHelpers';

import { themeColors } from 'themes/clients/baseTheme';

import { routes } from './routes';
import theme from './theme';

// ============================================
// Type Exports for Const Objects
// ============================================

export type AcknowledgementStatus = (typeof ACKNOWLEDGEMENT_STATUS)[keyof typeof ACKNOWLEDGEMENT_STATUS];
export type Actions = (typeof ACTIONS)[keyof typeof ACTIONS];
export type AddPostModalTypes = (typeof ADD_POST_MODAL_TYPES)[keyof typeof ADD_POST_MODAL_TYPES];
export type AddText = (typeof ADD_TEXT)[keyof typeof ADD_TEXT];
export type AgendaStatuses = (typeof AGENDA_STATUSES)[keyof typeof AGENDA_STATUSES];
export type AnalyticsFiltersKeys = (typeof ANALYTICS_FILTERS_KEYS)[keyof typeof ANALYTICS_FILTERS_KEYS];
export type AppSourceContainers = (typeof APP_SOURCE_CONTAINERS)[keyof typeof APP_SOURCE_CONTAINERS];
export type BulkUploadPrivileges = (typeof BULK_UPLOAD_PRIVILEGES)[keyof typeof BULK_UPLOAD_PRIVILEGES];
export type CheckinQuestionTypes = (typeof CHECKIN_QUESTION_TYPES)[keyof typeof CHECKIN_QUESTION_TYPES];
export type CohortsIdMapping = (typeof COHORTS_ID_MAPPING)[keyof typeof COHORTS_ID_MAPPING];
export type CohortCreationPageType = (typeof COHORT_CREATION_PAGE_TYPE)[keyof typeof COHORT_CREATION_PAGE_TYPE];
export type CohortTypesEngagement = (typeof COHORT_TYPES_ENGAGEMENT)[keyof typeof COHORT_TYPES_ENGAGEMENT];
export type CohortTypesFeedback = (typeof COHORT_TYPES_FEEDBACK)[keyof typeof COHORT_TYPES_FEEDBACK];
export type CohortTypesGoals = (typeof COHORT_TYPES_GOALS)[keyof typeof COHORT_TYPES_GOALS];
export type CohortTypesOneOnOne = (typeof COHORT_TYPES_ONE_ON_ONE)[keyof typeof COHORT_TYPES_ONE_ON_ONE];
export type CohortTypesReview = (typeof COHORT_TYPES_REVIEW)[keyof typeof COHORT_TYPES_REVIEW];
export type CompetencySidepanelType = (typeof COMPETENCY_SIDEPANEL_TYPE)[keyof typeof COMPETENCY_SIDEPANEL_TYPE];
export type ContinuousFeedbackFormStatus =
  (typeof CONTINUOUS_FEEDBACK_FORM_STATUS)[keyof typeof CONTINUOUS_FEEDBACK_FORM_STATUS];
export type ContinuousFeedbackPeerStatus =
  (typeof CONTINUOUS_FEEDBACK_PEER_STATUS)[keyof typeof CONTINUOUS_FEEDBACK_PEER_STATUS];
export type ContinuousFeedbackReloadType =
  (typeof CONTINUOUS_FEEDBACK_RELOAD_TYPE)[keyof typeof CONTINUOUS_FEEDBACK_RELOAD_TYPE];
export type CreateKeyResultPrivileges =
  (typeof CREATE_KEY_RESULT_PRIVILEGES)[keyof typeof CREATE_KEY_RESULT_PRIVILEGES];
export type CreateObjectivePrivileges = (typeof CREATE_OBJECTIVE_PRIVILEGES)[keyof typeof CREATE_OBJECTIVE_PRIVILEGES];
export type DisciplinaryActionsGraphTypes =
  (typeof DISCIPLINARY_ACTIONS_GRAPH_TYPES)[keyof typeof DISCIPLINARY_ACTIONS_GRAPH_TYPES];
export type EngagementAnswerTypes = (typeof ENGAGEMENT_ANSWER_TYPES)[keyof typeof ENGAGEMENT_ANSWER_TYPES];
export type EngagementTemplateMenuOptions =
  (typeof ENGAGEMENT_TEMPLATE_MENU_OPTIONS)[keyof typeof ENGAGEMENT_TEMPLATE_MENU_OPTIONS];
export type FeatureKeys = (typeof FEATURE_KEYS)[keyof typeof FEATURE_KEYS];
export type FeedbackFormType = (typeof FEEDBACK_FORM_TYPE)[keyof typeof FEEDBACK_FORM_TYPE];
export type FeedbackInsightsViewIds = (typeof FEEDBACK_INSIGHTS_VIEW_IDS)[keyof typeof FEEDBACK_INSIGHTS_VIEW_IDS];
export type FeedbackStrengthTypes = (typeof FEEDBACK_STRENGTH_TYPES)[keyof typeof FEEDBACK_STRENGTH_TYPES];
export type GenderChartColors = (typeof GENDER_CHART_COLORS)[keyof typeof GENDER_CHART_COLORS];
export type GenderMapping = (typeof GENDER_MAPPING)[keyof typeof GENDER_MAPPING];
export type GoalAnalyticsViewIds = (typeof GOAL_ANALYTICS_VIEW_IDS)[keyof typeof GOAL_ANALYTICS_VIEW_IDS];
export type GoalProgressesFullLabel = (typeof GOAL_PROGRESSES_FULL_LABEL)[keyof typeof GOAL_PROGRESSES_FULL_LABEL];
export type GoalProgressesPartialLabel =
  (typeof GOAL_PROGRESSES_PARTIAL_LABEL)[keyof typeof GOAL_PROGRESSES_PARTIAL_LABEL];
export type GoalProgressChartColors = (typeof GOAL_PROGRESS_CHART_COLORS)[keyof typeof GOAL_PROGRESS_CHART_COLORS];
export type GoalProgressEntities = (typeof GOAL_PROGRESS_ENTITIES)[keyof typeof GOAL_PROGRESS_ENTITIES];
export type HelloBarPageType = (typeof HELLO_BAR_PAGE_TYPE)[keyof typeof HELLO_BAR_PAGE_TYPE];
export type HelloBarPageType2 = (typeof HELLO_BAR_PAGE_TYPE_2)[keyof typeof HELLO_BAR_PAGE_TYPE_2];
export type LandingPageTypes = (typeof LANDING_PAGE_TYPES)[keyof typeof LANDING_PAGE_TYPES];
export type LoginTypes = (typeof LOGIN_TYPES)[keyof typeof LOGIN_TYPES];
export type MeshRecommendationsTypes = (typeof MESH_RECOMMENDATIONS_TYPES)[keyof typeof MESH_RECOMMENDATIONS_TYPES];
export type ModuleKeys = (typeof MODULE_KEYS)[keyof typeof MODULE_KEYS];
export type ModuleKeys1 = (typeof MODULE_KEYS1)[keyof typeof MODULE_KEYS1];
export type NominationPanelClosingStatus =
  (typeof NOMINATION_PANEL_CLOSING_STATUS)[keyof typeof NOMINATION_PANEL_CLOSING_STATUS];
export type ObjectiveCreationPlaceholderText =
  (typeof OBJECTIVE_CREATION_PLACEHOLDER_TEXT)[keyof typeof OBJECTIVE_CREATION_PLACEHOLDER_TEXT];
export type ObjectiveLevel = (typeof OBJECTIVE_LEVEL)[keyof typeof OBJECTIVE_LEVEL];
export type OkrProgressTypes = (typeof OKR_PROGRESS_TYPES)[keyof typeof OKR_PROGRESS_TYPES];
export type OkrTagTypes = (typeof OKR_TAG_TYPES)[keyof typeof OKR_TAG_TYPES];
export type OkrTypes = (typeof OKR_TYPES)[keyof typeof OKR_TYPES];
export type OneOnOneInstanceViews = (typeof ONE_ON_ONE_INSTANCE_VIEWS)[keyof typeof ONE_ON_ONE_INSTANCE_VIEWS];
export type OneOnOneStatuses = (typeof ONE_ON_ONE_STATUSES)[keyof typeof ONE_ON_ONE_STATUSES];
export type OneOnOneTimelineViews = (typeof ONE_ON_ONE_TIMELINE_VIEWS)[keyof typeof ONE_ON_ONE_TIMELINE_VIEWS];
export type OrgId = (typeof ORG_ID)[keyof typeof ORG_ID];
export type PageTypes = (typeof PAGE_TYPES)[keyof typeof PAGE_TYPES];
export type ParticipantsSelectionModules =
  (typeof PARTICIPANTS_SELECTION_MODULES)[keyof typeof PARTICIPANTS_SELECTION_MODULES];
export type PeerNominationCohorts = (typeof PEER_NOMINATION_COHORTS)[keyof typeof PEER_NOMINATION_COHORTS];
export type PeerTypeTagNameMap = ReturnType<typeof getPeerTypeTagNameMap>[keyof ReturnType<
  typeof getPeerTypeTagNameMap
>];
export type PlanType = (typeof PLAN_TYPE)[keyof typeof PLAN_TYPE];
export type PostBeforeOpenTextRabbit = (typeof POST_BEFORE_OPEN_TEXT_RABBIT)[keyof typeof POST_BEFORE_OPEN_TEXT_RABBIT];
export type PostModalPlaceholderTexts =
  (typeof POST_MODAL_PLACEHOLDER_TEXTS)[keyof typeof POST_MODAL_PLACEHOLDER_TEXTS];
export type PostModalPlaceholderTextsRabbit =
  (typeof POST_MODAL_PLACEHOLDER_TEXTS_RABBIT)[keyof typeof POST_MODAL_PLACEHOLDER_TEXTS_RABBIT];
export type ProgressComparisonTypes = (typeof PROGRESS_COMPARISON_TYPES)[keyof typeof PROGRESS_COMPARISON_TYPES];
export type PulseSurveyCreateStatus = (typeof PULSE_SURVEY_CREATE_STATUS)[keyof typeof PULSE_SURVEY_CREATE_STATUS];
export type PulseSurveyStatus = (typeof PULSE_SURVEY_STATUS)[keyof typeof PULSE_SURVEY_STATUS];
export type QuestionFormSources = (typeof QUESTION_FORM_SOURCES)[keyof typeof QUESTION_FORM_SOURCES];
export type QuestionMenuOptions = (typeof QUESTION_MENU_OPTIONS)[keyof typeof QUESTION_MENU_OPTIONS];
export type QuestionOrderActions = (typeof QUESTION_ORDER_ACTIONS)[keyof typeof QUESTION_ORDER_ACTIONS];
export type QuestionType = (typeof QUESTION_TYPE)[keyof typeof QUESTION_TYPE];
export type RatingsTypes = (typeof RATINGS_TYPES)[keyof typeof RATINGS_TYPES];
export type Reactions = (typeof REACTIONS)[keyof typeof REACTIONS];
export type RecurrentSubTaskTypes = (typeof RECURRENT_SUB_TASK_TYPES)[keyof typeof RECURRENT_SUB_TASK_TYPES];
export type ReviewAnswerTypeMap = (typeof REVIEW_ANSWER_TYPE_MAP)[keyof typeof REVIEW_ANSWER_TYPE_MAP];
export type ReviewCycleRatingSplitColorMapping =
  (typeof REVIEW_CYCLE_RATING_SPLIT_COLOR_MAPPING)[keyof typeof REVIEW_CYCLE_RATING_SPLIT_COLOR_MAPPING];
export type ReviewFormsTypeMap = (typeof REVIEW_FORMS_TYPE_MAP)[keyof typeof REVIEW_FORMS_TYPE_MAP];
export type ReviewFormCompletionStatus =
  (typeof REVIEW_FORM_COMPLETION_STATUS)[keyof typeof REVIEW_FORM_COMPLETION_STATUS];
export type ReviewInfoFeedbackFormCurrentStage =
  (typeof REVIEW_INFO_FEEDBACK_FORM_CURRENT_STAGE)[keyof typeof REVIEW_INFO_FEEDBACK_FORM_CURRENT_STAGE];
export type ReviewPeerStatus = (typeof REVIEW_PEER_STATUS)[keyof typeof REVIEW_PEER_STATUS];
export type ReviewProviderTypes = (typeof REVIEW_PROVIDER_TYPES)[keyof typeof REVIEW_PROVIDER_TYPES];
export type ReviewQuestionTypes = (typeof REVIEW_QUESTION_TYPES)[keyof typeof REVIEW_QUESTION_TYPES];
export type ReviewQuestionTypeMap1 = (typeof REVIEW_QUESTION_TYPE_MAP_1)[keyof typeof REVIEW_QUESTION_TYPE_MAP_1];
export type ReviewQuestionTypeMap2 = (typeof REVIEW_QUESTION_TYPE_MAP_2)[keyof typeof REVIEW_QUESTION_TYPE_MAP_2];
export type ReviewQuestionTypeMap3 = (typeof REVIEW_QUESTION_TYPE_MAP_3)[keyof typeof REVIEW_QUESTION_TYPE_MAP_3];
export type ReviewResponseProviderTypeTitleMap = ReturnType<
  typeof getReviewResponseProviderTypeTitleMap1
>[keyof ReturnType<typeof getReviewResponseProviderTypeTitleMap1>];
export type ReviewResponseProviderTypeTitleMap1 = ReturnType<
  typeof getReviewResponseProviderTypeTitleMap1
>[keyof ReturnType<typeof getReviewResponseProviderTypeTitleMap1>];
export type ReviewStages = (typeof REVIEW_STAGES)[keyof typeof REVIEW_STAGES];
export type ReviewTemplateMenuOptions =
  (typeof REVIEW_TEMPLATE_MENU_OPTIONS)[keyof typeof REVIEW_TEMPLATE_MENU_OPTIONS];
export type SearchTagTypes = (typeof SEARCH_TAG_TYPES)[keyof typeof SEARCH_TAG_TYPES];
export type SearchWidgetTypes = (typeof SEARCH_WIDGET_TYPES)[keyof typeof SEARCH_WIDGET_TYPES];
export type Sentiments = (typeof SENTIMENTS)[keyof typeof SENTIMENTS];
export type SettingsRouteForHeader = (typeof SETTINGS_ROUTE_FOR_HEADER)[keyof typeof SETTINGS_ROUTE_FOR_HEADER];
export type TagIdType = (typeof TAG_ID_TYPE)[keyof typeof TAG_ID_TYPE];
export type TaskDetailedStatus = (typeof TASK_DETAILED_STATUS)[keyof typeof TASK_DETAILED_STATUS];
export type TaskEmployeeTypes = (typeof TASK_EMPLOYEE_TYPES)[keyof typeof TASK_EMPLOYEE_TYPES];
export type TaskOptionId = (typeof TASK_OPTION_ID)[keyof typeof TASK_OPTION_ID];
export type TaskTypes = (typeof TASK_TYPES)[keyof typeof TASK_TYPES];
export type TaskTypes1 = (typeof TASK_TYPES_1)[keyof typeof TASK_TYPES_1];
export type TaskTypes2 = (typeof TASK_TYPES_2)[keyof typeof TASK_TYPES_2];
export type TeamhubLastActivityDaterangeEnumMap =
  (typeof TEAMHUB_LAST_ACTIVITY_DATERANGE_ENUM_MAP)[keyof typeof TEAMHUB_LAST_ACTIVITY_DATERANGE_ENUM_MAP];
export type TeamDurationsObject = (typeof TEAM_DURATIONS_OBJECT)[keyof typeof TEAM_DURATIONS_OBJECT];
export type TeamReviewTexts = (typeof TEAM_REVIEW_TEXTS)[keyof typeof TEAM_REVIEW_TEXTS];
export type TeamTasksViewNames = (typeof TEAM_TASKS_VIEW_NAMES)[keyof typeof TEAM_TASKS_VIEW_NAMES];
export type TeamTasksViewTypes = (typeof TEAM_TASKS_VIEW_TYPES)[keyof typeof TEAM_TASKS_VIEW_TYPES];
export type TeamType = (typeof TEAM_TYPE)[keyof typeof TEAM_TYPE];
export type TeamVisibility = (typeof TEAM_VISIBILITY)[keyof typeof TEAM_VISIBILITY];
export type ToastStatuses = (typeof TOAST_STATUSES)[keyof typeof TOAST_STATUSES];
export type TrackingOptionsMap = (typeof TRACKING_OPTIONS_MAP)[keyof typeof TRACKING_OPTIONS_MAP];
export type TransactionSummaryTypes = (typeof TRANSACTION_SUMMARY_TYPES)[keyof typeof TRANSACTION_SUMMARY_TYPES];
export type UniversalSearchResultsLimit =
  (typeof UNIVERSAL_SEARCH_RESULTS_LIMIT)[keyof typeof UNIVERSAL_SEARCH_RESULTS_LIMIT];
export type UniversalSearchResultsLimitMobile =
  (typeof UNIVERSAL_SEARCH_RESULTS_LIMIT_MOBILE)[keyof typeof UNIVERSAL_SEARCH_RESULTS_LIMIT_MOBILE];
export type UserLoginEventsActions = (typeof USER_LOGIN_EVENTS_ACTIONS)[keyof typeof USER_LOGIN_EVENTS_ACTIONS];
export type ViewsDynamicTypesLabels = (typeof VIEWS_DYNAMIC_TYPES_LABELS)[keyof typeof VIEWS_DYNAMIC_TYPES_LABELS];
export type ViewsDynamicTypesValues = (typeof VIEWS_DYNAMIC_TYPES_VALUES)[keyof typeof VIEWS_DYNAMIC_TYPES_VALUES];
export type ViewsFilterKeys = (typeof VIEWS_FILTER_KEYS)[keyof typeof VIEWS_FILTER_KEYS];
export type ViewsFilterKeysDisplayNames =
  (typeof VIEWS_FILTER_KEYS_DISPLAY_NAMES)[keyof typeof VIEWS_FILTER_KEYS_DISPLAY_NAMES];
export type ViewsVisibilityTypesLabels =
  (typeof VIEWS_VISIBILITY_TYPES_LABELS)[keyof typeof VIEWS_VISIBILITY_TYPES_LABELS];
export type ViewsVisibilityTypesValues =
  (typeof VIEWS_VISIBILITY_TYPES_VALUES)[keyof typeof VIEWS_VISIBILITY_TYPES_VALUES];
export type ViewDownloadInsightPrivileges =
  (typeof VIEW_DOWNLOAD_INSIGHT_PRIVILEGES)[keyof typeof VIEW_DOWNLOAD_INSIGHT_PRIVILEGES];
export type VisibleProviderTypesColorMapping =
  (typeof VISIBLE_PROVIDER_TYPES_COLOR_MAPPING)[keyof typeof VISIBLE_PROVIDER_TYPES_COLOR_MAPPING];
export type VisierDataExportRooms = (typeof VISIER_DATA_EXPORT_ROOMS)[keyof typeof VISIER_DATA_EXPORT_ROOMS];
export type VisierNameMapping = (typeof VISIER_NAME_MAPPING)[keyof typeof VISIER_NAME_MAPPING];
export type AnalyticalQuestionType = (typeof ANALYTICAL_QUESTION_TYPE)[keyof typeof ANALYTICAL_QUESTION_TYPE];
export type SelectedQuestionPills = (typeof SELECTED_QUESTION_PILLS)[keyof typeof SELECTED_QUESTION_PILLS];
export type QuestionFormType = (typeof QUESTION_FORM_TYPE)[keyof typeof QUESTION_FORM_TYPE];
export type QuestionMenuAction = (typeof QUESTION_MENU_ACTION)[keyof typeof QUESTION_MENU_ACTION];

// ============================================
// Constants
// ============================================

export const supportEmail = 'support@mesh.ai';
export const contactEmail = 'contactus@mesh.ai';

export const cookieKeys = {
  employeeDetails: 'employeeDetails',
} as const;

export const PROGRESS_COMPARISON_TYPES = {
  peer: 'MY_PEERS',
  team: 'MY_TEAM',
  organization: 'ORGANIZATION',
} as const;

export const PROGRESS_COMPARISON_DROPDOWN_DATA = [
  {
    name: 'My Peers',
    value: PROGRESS_COMPARISON_TYPES.peer,
  },
  {
    name: 'My Team',
    value: PROGRESS_COMPARISON_TYPES.team,
  },
  {
    name: 'Organization',
    value: PROGRESS_COMPARISON_TYPES.organization,
  },
];

export const SEARCH_TAG_TYPES = {
  team: 'TEAM',
  employee: 'EMPLOYEE_NAME',
  tag: 'ORGTAG',
  location: 'LOCATION',
  goal: 'GOAL',
  goalTag: 'GOAL_TAG',
  taskTag: 'MESH_TASK_TAG',
  subObjectiveTag: 'SUB_OBJECTIVE_TAG',
  objectiveTimeBound: 'OBJECTIVE_TIME_BOUND',
} as const;

export const MODULE_KEYS = {
  GOAL: 'GOAL',
  TASK: 'TASK',
  REVIEW: 'REVIEW',
  ENGAGEMENT: 'ENGAGEMENT',
  DISCIPLINARY_ACTIONS: 'DISCIPLINARY_ACTIONS',
  ONE_ON_ONE: 'ONE_ON_ONE',
  FEEDBACK: 'FEEDBACK',
  LEVEL_UP: 'LEVEL_UP',
  COMPENSATION: 'COMPENSATION',
  VIOLATION_POINTS: 'VIOLATION_POINTS',
} as const;

export const MODULE_KEYS1 = {
  GOAL: 'goal',
  INITIATIVE: 'initiative',
  REVIEW: 'status',
  ENGAGEMENT: 'engagement',
  ONE_ON_ONE: 'oneOnOne',
  DEVELOP: 'levelUp',
  FEEDBACK: 'competency',
  OLD_INSIGHTS_PAGE: 'oldInsightsPage',
  MODULE_SETTINGS: 'moduleSettings',
  DUMMY_INITIATIVE: 'dummyInitiative',
  CONTINUOUS_FEEDBACK: 'continuousFeedback',
  DISCIPLINARY_ACTIONS: 'disciplinaryActions',
} as const;

export const SIDE_NAV_MATCH_ROUTES = [
  // goal module
  {
    module: MODULE_KEYS1.GOAL,
    routes: [
      routes.goals.index,
      routes.goals.audit,
      routes.goals.list,
      routes.goals.card,
      routes.goals.tree,
      routes.goals.insights,
      routes.analytics.goals,
      routes.goals.archived,
      routes.embeddedAnalytics.index.replace(':id', 'goals'),
    ],
  },

  // initiatives module
  {
    module: MODULE_KEYS1.INITIATIVE,
    routes: [routes.initiative.index, routes.initiative.audit, routes.initiative.card],
  },

  // 1:1
  {
    module: MODULE_KEYS1.ONE_ON_ONE,
    routes: [
      routes.oneOnOne.index,
      routes.oneOnOne.audit,
      routes.oneOnOne.insights,
      routes.analytics.oneOnOne,
      routes.settings.checkIn.questions,
      routes.settings.checkIn.templates,
      routes.settings.checkIn.preferences,
      routes.settings.checkIn.settings,
      routes.oneOnOne.instanceRabbit,
      routes.embeddedAnalytics.index.replace(':id', 'oneOnOne'),
    ],
  },

  // develop
  {
    module: MODULE_KEYS1.DEVELOP,
    routes: [routes.levelUp.index, routes.analytics.levelUp, routes.levelUp.myTeam, routes.levelUp.myTeamInsights],
  },
  {
    module: MODULE_KEYS1.CONTINUOUS_FEEDBACK,
    routes: [
      routes.continuousFeedback.index,
      routes.continuousFeedback.myPraisesAndAdvices,
      routes.analytics.continuousFeedback,
    ],
  },
  // review
  {
    module: MODULE_KEYS1.REVIEW,
    routes: [
      routes.performanceReview.index,
      routes.performanceReview.audit,
      routes.performanceReview.performancePreview,
      routes.performanceReview.manage,
      routes.performanceReview.talentPlanning,
      routes.performanceReview.insights,
      // routes.performanceReview.teamStatus,
      routes.settings.review.reviewCycle,
      routes.performanceReview.forms,
      routes.performanceReview.result,
      routes.embeddedAnalytics.index.replace(':id', 'reviews'),
    ],
  },

  // engagement
  {
    module: MODULE_KEYS1.ENGAGEMENT,
    routes: [
      routes.engagement.index,
      routes.analytics.engagement,
      routes.engagement.manage,
      routes.engagement.insights,
    ],
  },

  { module: MODULE_KEYS1.MODULE_SETTINGS, routes: [routes.moduleSettings] },

  // disciplinary actions
  {
    module: MODULE_KEYS1.DISCIPLINARY_ACTIONS,
    routes: [routes.disciplinaryActions.pending, routes.disciplinaryActions.all],
  },
];

// export const OLD_INSIGHT_PAGE_ROUTES = [
//   { name: 'Goals', route: routes.analytics.goals ,featureKey: FEATURE_KEYS.GOAL_ANALYTICS},
//   { name: '1:1 Meetings', route: routes.analytics.oneOnOne ,featureKey: FEATURE_KEYS.ONE_ON_ONE_ANALYTICS},
//   { name: 'Review', route: routes.analytics.review ,featureKey: FEATURE_KEYS.RE},
//   { name: 'Engagement', route: routes.analytics.engagement ,featureKey: FEATURE_KEYS.GOAL},
//   { name: 'Develop', route: routes.analytics.levelUp ,featureKey: FEATURE_KEYS.GOAL},
//   { name: 'Feedback', route: routes.analytics.feedback ,featureKey: FEATURE_KEYS.GOAL}
// ];

export const COHORT_CREATION_PAGE_TYPE = {
  [MODULE_KEYS1.GOAL]: 'GOAL',
  [MODULE_KEYS1.INITIATIVE]: 'INITIATIVE',
  [MODULE_KEYS1.ONE_ON_ONE]: 'ONE_ON_ONE',
  [MODULE_KEYS1.ENGAGEMENT]: 'ENGAGEMENT',
  [MODULE_KEYS1.FEEDBACK]: 'CORE_COMPETENCY',
  [MODULE_KEYS1.REVIEW]: 'REVIEW',
  [MODULE_KEYS1.CONTINUOUS_FEEDBACK]: 'CONTINUOUS_FEEDBACK',
  [MODULE_KEYS1.DEVELOP]: 'LEVEL_UP',
  STATUS: 'STATUS',
  CALIBRATION: 'CALIBRATION',
} as const;

export const SETTINGS_ROUTE_FOR_HEADER = {
  goals: [
    routes.goals.index,
    // routes.settings.objectiveSettings
  ],
  initiatives: [
    routes.initiative.index,
    // routes.settings.meshTags
  ],
  oneOnOne: [
    routes.oneOnOne.index,
    // routes.settings.checkIn.questions,
    // routes.settings.checkIn.templates,
    // routes.settings.checkIn.preferences,
    // routes.settings.checkIn.settings,
    routes.analytics.oneOnOne,
  ],
  review: [
    routes.performanceReview.index,
    // routes.settings.review.questions,
    // routes.settings.review.reviewTemplate.list,
    // routes.settings.review.reviewProcess,
    // routes.settings.review.reviewSettings,
  ],
  engagement: [
    routes.engagement.index,
    // routes.settings.engagement.drivers,
    // routes.settings.engagement.questions,
    // routes.settings.engagement.templates,
    // routes.settings.engagement.pulseSurvey,
    routes.analytics.engagement,
  ],
  levelUp: [routes.levelUp.index, routes.levelUp.myTeam, routes.analytics.levelUp],
  continuousFeedback: [
    routes.continuousFeedback.index,
    routes.continuousFeedback.myPraisesAndAdvices,
    routes.analytics.continuousFeedback,
  ],
  embeddedAnalytics: [routes.embeddedAnalytics.index],
} as const;

export const SENTIMENTS = {
  constructive: 'CONSTRUCTIVE',
  appreciative: 'APPRECIATIVE',
  neutral: 'NEUTRAL',
} as const;

export const POST_VISIBILITY_TYPES = {
  global: {
    name: 'GLOBAL',
    displayName: 'Public',
  },
  private: {
    name: 'PRIVATE',
    displayName: 'Private',
  },
} as const;

export const TEAM_DURATIONS_OBJECT = {
  yesterday: 'YESTERDAY',
  lastWeek: 'LASTWEEK',
  last2Weeks: 'LAST2WEEKS',
  lastMonth: 'LASTMONTH',
} as const;

export const TEAM_DURATION_VALUES = [
  { name: 'Since Yesterday', value: 'YESTERDAY' },
  { name: 'Since Last Week', value: 'LASTWEEK' },
  { name: 'Since Last 2 Weeks', value: 'LAST2WEEKS' },
  { name: 'Since Last Month', value: 'LASTMONTH' },
];

export const SEARCH_WIDGET_TYPES = {
  search: 'SEARCH',
  post: 'POST',
  comment: 'COMMENT',
  task: 'TASK',
  goal: 'GOAL',
  primaryPod: 'PRIMARY_POD',
  grade: 'GRADE',
  function: 'FUNCTION',
} as const;

export const TRANSACTION_SUMMARY_TYPES = {
  received: 'RECEIVED',
  shared: 'SHARED',
} as const;

export const TASK_TYPES = { active: 'ACTIVE', completed: 'COMPLETED' } as const;

export const SIZES_KEYS_ARRAY = ['smallest', 'smaller', 'small', 'smallMedium', 'medium', 'big'];

export const SEARCH_PLACEHOLDER = 'Search for team member';

export const TEAM_TASKS_VIEW_TYPES = {
  card: 'CARD',
  list: 'LIST',
  goalTree: 'TREE',
} as const;
export const TEAM_TASKS_VIEW_NAMES = {
  [TEAM_TASKS_VIEW_TYPES.card]: 'Card View',
  [TEAM_TASKS_VIEW_TYPES.list]: 'List View',
  [TEAM_TASKS_VIEW_TYPES.goalTree]: 'Tree View',
} as const;

// ################ placeholders and texts ###################
export const CREATE_GOAL_TASK_PLACEHOLDER = 'Key result to be achieved ...';
export const CREATE_TASK_PLACEHOLDER = 'Will be working on';
export const ADD_POST_PLACEHOLDER = 'What would you like to share?';
export const ADD_TEXT = {
  task: 'Create new task',
  todo: 'Create new to-do',
  goal: 'Create new goal',
} as const;

// Names of common entities of the project
// Making them constant because they kept changing
export const MESH_IN_DISPLAY_TEXT = 'Portfolio';
export const MESH_UP_DISPLAY_TEXT = 'My Impact';
export const MESH_TEAM_DISPLAY_TEXT = 'Team Impact';

// Error messages
export const ERR_MESS_CREATE_COMMENT = 'An error occurred while creating comment';
export const ERROR_MESSAGE = 'Sorry an error occurred while fetching the data';
export const ERROR_MESSAGE_ACTION = 'Sorry an error occurred while performing the action. Please try again later!';
export const CREATE_POST_ERROR = "Sorry couldn't create post, you have not provided correct data!";
export const UPDATE_POST_ERROR = "Sorry couldn't update post";

export const CREATE_POST_SUCCESS = 'Successfully created a public post!';
export const POST_PRAISE_SUCCESS = 'Successfully shared praise with everyone!';
export const POST_FEEDBACK_SUCCESS = 'Successfully shared!';

export const UPDATE_POST_SUCCESS = 'Successfully updated post.';
export const IMAGE_UPLOAD_ERROR = 'An error occurred while uploading the image';
export const ERROR_ADD_APPLAUD_FEED = 'Error applauding the post';
export const ERROR_ADD_LIKE_FEED = 'Error liking the post';
export const ERROR_ADD_APPLAUD_TASK = 'Error applauding the task';
export const ERROR_ADD_LIKE_TASK = 'Error applauding the task';

// ---- Comment -------
export const COMMENT_EDIT_ERROR = 'Sorry an error occurred while updating comment';

// ----------

// Success Messages
export const IMAGE_UPLOAD_SUCCESS = 'Successfully uploaded the image';
export const SUCCESS_ADD_APPLAUD_FEED = 'Successfully applauded the post';
export const SUCCESS_ADD_LIKE_FEED = 'Successfully liked the post';
export const SUCCESS_ADD_APPLAUD_TASK = 'Successfully applauded the task';
export const SUCCESS_ADD_LIKE_TASK = 'Successfully liked the task';

// -------------

// No data component texts
export const MESH_WIN_NO_DATA = 'Currently, you have no trophies!';
export const FEEDBACKS_NO_DATA = 'No feedbacks yet.';
export const DEPARTMENTS = [
  'Biotechnology',
  'Finance',
  'HR',
  'Manufacturing',
  'Marketing',
  // 'Medical equipments',
  'Operations',
  // 'Pharmaceutical',
  'Production',
  'Sales',
  'Strategy',
  'Technology',
  'Others (Design/R&D etc.)',
];

// need to add support for 'other' genders
export const GENDERS = {
  MALE: {
    key: 'MALE',
    label: 'Male',
    label2: 'Men',
  },
  FEMALE: {
    key: 'FEMALE',
    label: 'Female',
    label2: 'Women',
  },
  OTHER: {
    key: 'OTHER',
    label: 'Other',
    label2: 'Others',
  },
};

export const GOAL_ADMIN = 'GOAL_ADMIN';
export const GOAL_ADMIN_LABEL = 'Goals';

export const ONE_ON_ONE_ADMIN = 'ONE_ON_ONE_ADMIN';
export const ONE_ON_ONE_ADMIN_LABEL = '1:1';

export const REVIEW_ADMIN = 'REVIEW_ADMIN';
export const REVIEW_ADMIN_LABEL = 'Reviews';

export const ENGAGEMENT_ADMIN = 'ENGAGEMENT_ADMIN';
export const ENGAGEMENT_ADMIN_LABEL = 'Engagement';

export const FEEDBACK_ADMIN = 'FEEDBACK_ADMIN';
export const FEEDBACK_ADMIN_LABEL = 'Feedback';

export const PLATFORM_ADMIN = 'PLATFORM_ADMIN';
export const PLATFORM_ADMIN_LABEL = 'Platform';

export const TASK_ADMIN = 'TASK_ADMIN';
export const TASK_ADMIN_LABEL = 'Initiatives';

// api value to be sent in edit employee access
export const DISCIPLINARY_ACTION_ADMIN = 'DISCIPLINARY_ACTION_ADMIN';
export const DISCIPLINARY_ACTION_ADMIN_LABEL = 'Disciplinary Actions';

export const COMPENSATION_ADMIN = 'COMPENSATION_ADMIN';

export const CUSTOM_ADMIN = 'CUSTOM_ADMIN';

export const ADMIN_ROLES_MAP_ORDER = {
  PLATFORM_ADMIN: {
    order: 0,
  },
  GOAL_ADMIN: {
    order: 1,
  },
  ONE_ON_ONE_ADMIN: {
    order: 2,
  },
  REVIEW_ADMIN: {
    order: 3,
  },
  ENGAGEMENT_ADMIN: {
    order: 4,
  },
  FEEDBACK_ADMIN: {
    order: 5,
  },
  TASK_ADMIN: {
    order: 6,
  },
  DISCIPLINARY_ACTION_ADMIN: {
    order: 7,
  },
};

export const FEATURE_KEYS = {
  ENGAGEMENT: 'ENGAGEMENT',
  DISCIPLINARY_ACTIONS: 'DISCIPLINARY_ACTION',
  REVIEW: 'REVIEW',
  GOAL: 'GOAL',
  CHECK_IN: 'CHECK_IN',
  TASK: 'TASK',
  FEEDBACK: 'FEEDBACK',
  INITIATIVE: 'INITIATIVE',

  CALIBRATION_ANALYTICS: 'CALIBRATION_ANALYTICS',
  STATUS_ANALYTICS: 'STATUS_ANALYTICS',
  GOAL_OBJECTIVE_ANALYTICS: 'GOAL_OBJECTIVE_ANALYTICS',
  GOAL_ANALYTICS: 'GOAL_ANALYTICS',
  ONE_ON_ONE_ANALYTICS: 'ONE_ON_ONE_ANALYTICS',
  FEEDBACK_ANALYTICS: 'FEEDBACK_ANALYTICS',
  ENGAGEMENT_ANALYTICS: 'ENGAGEMENT_ANALYTICS',

  PODS: 'PODS',
  COHORT_CREATION_ANALYTICS: 'COHORT_CREATION_ANALYTICS',
  TEAM_SUMMARY: 'TEAM_SUMMARY',
  GROWTH: 'GROWTH',

  LEVEL_UP: 'LEVEL_UP',
  OVERVIEW: 'OVERVIEW',

  VIOLATION_POINTS: 'VIOLATION_POINTS',
} as const;

export const USER_ROLES = ({ enabledFeatures = [] }) => {
  // returns array of user roles applicable by checking which features are enabled for the org
  const userRoles = [];
  userRoles.push({
    value: PLATFORM_ADMIN,
    label: 'Platform',
  });
  if (enabledFeatures?.includes(FEATURE_KEYS.GOAL)) {
    userRoles.push({
      value: GOAL_ADMIN,
      label: 'Goals',
    });
  }
  if (enabledFeatures?.includes(FEATURE_KEYS.CHECK_IN)) {
    userRoles.push({
      value: ONE_ON_ONE_ADMIN,
      label: '1:1',
    });
  }
  if (enabledFeatures?.includes(FEATURE_KEYS.REVIEW)) {
    userRoles.push({
      value: REVIEW_ADMIN,
      label: 'Reviews',
    });
  }
  if (enabledFeatures?.includes(FEATURE_KEYS.ENGAGEMENT)) {
    userRoles.push({
      value: ENGAGEMENT_ADMIN,
      label: 'Engagement',
    });
  }
  if (enabledFeatures?.includes(FEATURE_KEYS.FEEDBACK)) {
    userRoles.push({
      value: FEEDBACK_ADMIN,
      label: 'Feedback',
    });
  }
  if (enabledFeatures?.includes(FEATURE_KEYS.INITIATIVE)) {
    userRoles.push({
      value: TASK_ADMIN,
      label: 'Initiatives',
    });
  }

  // api value coming in the edit employee access
  if (enabledFeatures?.includes(FEATURE_KEYS.DISCIPLINARY_ACTIONS)) {
    userRoles.push({
      value: DISCIPLINARY_ACTION_ADMIN,
      label: 'Disciplinary Actions',
    });
  }
  return userRoles;
};

// todo: refactor - move?
export const updateEmployeeFields = [
  'aggregatedValues',
  'createdTS',
  'updatedTS',
  'externalEmployeeId',
  'firstName',
  'lastName',
  'displayName',
  'designation',
  'location',
  'gender',
  'joiningDate',
  'resignationDate',
  'department',
  'tenure',
  'email',
  'country',
  'preferredFirstName',
  'dateOfBirth',
  'managerEmail',
  'userRole',
  'primaryPod',
  'secondaryPods',
  'employeeFunction',
  'grade',
  'ethnicity',
  'jobFunctionId',
  'jobGradeUI',
  'jobGradeNameUI',
  'jobRoleId', // not a good approach, maybe in next project we should use better way of updating user. Right now to add 1 more employee field lot of files have to be updated.
  'employeeAccesses',
  'jobGradeNameUI',
  'preferredName',
  'experienceBeforeJoining',
  'customFields',
  'companyId',
];

export const REFERRAL_SHARE_MESSAGE =
  'Mesh has made it super easy for my team to run effective sync ups and co-ordinate tasks at work. Sign up for free with my referral code - ';

export const HASHTAG_VALIDATION_REGEX = /^[a-zA-Z0-9\b]+$/;

export const USER_LOGIN_EVENTS_ACTIONS = {
  confirm: 'CONFIRM',
} as const;

export const SIGN_UP_ROUTES = [
  routes.login,
  routes.selectDepartment,
  routes.selectTeam,
  routes.invite,
  routes.emailRegister,
  routes.validateEmail,
  routes.forgotPassword,
  routes.forgotPasswordConfirmation,
  routes.selectOrganization,
];

// TODO: this implementation is a bit hackier, fix this with regex in v1.
export const GMAIL_ROUTES = [routes.gmail, `${routes.gmail}/`];
export const OUTLOOK_ROUTES = [routes.outlook.home, routes.outlook.login];
export const PASSWORD_HELP_TEXT =
  'Min. 8 characters with at least 1 upper case, lower case, number and special character';

export const SIGNUP_SELECT_DEPARTMENT_HELP_TEXT =
  'This will help us show you helpful examples of how best to use Mesh Now for managing work within your department.';

export const SIGNUP_CREATE_TEAM_HELP_TEXT =
  "What your pod is referred to as or liked to be called. The greatest superhero pod in the world is called 'Avengers'";

export const POST_BEFORE_OPEN_TEXT = 'Share Public Post';

export const POST_BEFORE_OPEN_TEXT_RABBIT = {
  CREATE_POST_PRAISE: 'Share Praise or Create New Post',
  CREATE_POST: 'Create New Post',
  CREATE_POST_OR_ADVICE: 'Share Praise or Feedback',
} as const;

export const ADD_POST_MODAL_TYPES = {
  CREATE_POST: 'CREATE_POST',
  GIVE_FEEDBACK: 'GIVE_FEEDBACK',
  GIVE_KUDOS: 'GIVE_KUDOS',
} as const;

export const POST_MODAL_PLACEHOLDER_TEXTS = {
  CREATE_POST: 'Create a post that is shared on everyone feed (Use @ to tag people and # to include competencies)',
  CREATE_POST_WITHOUT_COMPETENCY: 'Create a post that is shared on everyone feed (Use @ to tag people)',
  GIVE_KUDOS: 'Praise your coworker on something they did well',
  GIVE_FEEDBACK: 'Use @ to tag people and # to include a competency',
} as const;

export const POST_MODAL_PLACEHOLDER_TEXTS_RABBIT = {
  CREATE_POST: 'Create a post that is shared on everyone feed (Use @ to tag people and # to include competencies)',
  CREATE_POST_WITHOUT_COMPETENCY: 'Create a post that is shared on everyone feed (Use @ to tag people)',
  GIVE_KUDOS: 'Praise your coworker on something they did well',
  GIVE_FEEDBACK: 'Use @ to tag people and # to include a competency',
} as const;

export const POST_BEFORE_OPEN_TEXT_MOBILE = 'Create a post';

export const EMPTY_GROUP_TEXT = 'Unplanned Deliverables';

export const POST_MODAL_COMPETENCY_NOT_APPLICABLE_ERROR = 'Added competency is not applicable to these individuals.';

export const FILTERS = [
  {
    value: 'NAME',
    label: 'Employee Name',
    initial: 'N',
    color: 'rgba(0, 142, 240, 1)', // #008EF0
    bgColor: 'rgba(0, 142, 240, 0.15)',
  },
  {
    value: 'TEAM',
    label: 'Team',
    initial: 'T',
    color: 'rgba(255, 194, 0, 1)',
    bgColor: 'rgba(255, 194, 0, 0.15)',
  },
  {
    value: 'TAGS',
    label: 'Tag',
    initial: 'T',
    color: 'rgba(112, 116, 213, 1)', // #7074D5
    bgColor: 'rgba(112, 116, 213, 0.15)',
  },
  {
    value: 'GOALS',
    label: 'Objective',
    initial: 'O',
    color: 'rgba(255, 150, 74, 1)', // #ff964a
    bgColor: 'rgba(255, 150, 74, 0.15)',
  },
  {
    value: 'STATUS',
    label: 'Status',
    initial: 'S',
    color: 'rgba(247, 86, 64, 1)', // #F75640
    bgColor: 'rgba(247, 86, 64, 0.15)',
  },
  {
    value: 'ACTIVE_CONTENT',
    label: 'Due Date',
    initial: 'D',
    color: 'rgba(80, 183, 171, 1)', // #50B7AB
    bgColor: 'rgba(80, 183, 171, 0.15)',
  },
  {
    value: 'DURATION',
    label: 'Duration',
    initial: 'T',
    color: 'rgba(255, 194, 0, 1)',
    bgColor: 'rgba(255, 194, 0, 0.15)',
  },
  {
    value: 'PRIMARY_POD',
    label: 'Primary Pod',
    initial: 'P',
    color: 'rgba(255, 194, 0, 1)', // same as duration
    bgColor: 'rgba(255, 194, 0, 0.15)', //  same as duration
  },
  {
    value: 'TASK_TYPE',
    label: 'Task Type',
    initial: 'T',
    color: 'rgba(255, 194, 0, 1)', // same as duration
    bgColor: 'rgba(255, 194, 0, 0.15)', //  same as duration
  },
];

export const GOAL_PROGRESSES = [
  {
    label: 'On-Track',
    value: 'ON_TRACK',
    color: 'green',
    key: 'onTrackProgress',
  },
  {
    label: 'Off-Track',
    value: 'OFF_TRACK',
    color: 'orange',
    key: 'offTrackProgress',
  },
  {
    label: 'At-Risk',
    value: 'AT_RISK',
    color: 'red',
    key: 'atRiskProgress',
  },
  {
    label: 'Closed',
    value: 'CLOSED',
    color: 'primary',
    key: 'closedProgress',
  },
];

export const GOAL_PROGRESS_VALUE_TO_OBJECT_MAP = GOAL_PROGRESSES.reduce(
  (prev, curr) => ({
    // biome-ignore lint/performance/noAccumulatingSpread: Small fixed-size array, performance impact negligible
    ...prev,
    [curr.value]: curr,
  }),
  {}
);

export const STATS_DATA = {
  TASK: [
    {
      label: 'Completed',
      value: 'COMPLETED',
      color: 'green',
      key: 'completed', // this key is same as the one returned by api.
    },
    {
      label: 'Ongoing',
      value: 'ONGOING',
      color: 'orange',
      key: 'ongoing',
    },
    {
      label: 'Delayed',
      value: 'DELAYED',
      color: 'red',
      key: 'delayed',
    },
    {
      label: 'Unplanned',
      value: 'UNPLANNED',
      color: 'primary',
      key: 'unplanned',
    },
  ],
  GOAL: GOAL_PROGRESSES,
};

export const DATE_OPERATORS = [
  {
    label: 'Before',
    value: 'LESS_THAN',
  },
  { label: 'After', value: 'GREATER_THAN' },
  { label: 'Equals', value: 'EQUALS' },
];
export const PAGE_TYPES = {
  profile: 'PROFILE_PAGE',
  home: 'HOME_PAGE',
  review: 'REVIEW',
  oneOnOne: 'ONE_ON_ONE',
} as const;

export const REACTIONS = {
  applause: 'APPLAUSE',
  like: 'LIKE',
  comment: 'COMMENT',
} as const;

export const PREFERRED_TEAM_INFO_TEXT =
  'Preferred will always appear on top of the list in Tasks, Teams and Settings panel for easier management. Choose a pod which you would use most frequently as Preferred pod.';

export const NO_MULTIPLE_MENTIONS_ERROR = 'The task should have at most one employee mention';

export const UNIVERSAL_SEARCH_RESULTS_LIMIT = {
  team: 4,
  orgTag: 4,
  employeeName: 4,
} as const;

export const UNIVERSAL_SEARCH_RESULTS_LIMIT_MOBILE = {
  employeeName: 10,
} as const;

export const ORDERED_TAG_TYPES = ['TEAM', 'ORGTAG', 'EMPLOYEE_NAME', 'MESH_TASK_TAG', 'GOAL_TAG', 'DEPARTMENT'];

export const GLOBAL_TEAM_INFO_TEXT = (category) =>
  `${category.toProperCase()}s marked as global are visible to everyone within the organization`;

export const PRIVATE_TEAM_INFO_TEXT = (category) =>
  `${category.toProperCase()}s marked as private are visible only to the members of the ${category}`;

export const TASKS_LIST_HEADERS = [
  {
    id: 'STATUS',
    centerAlign: true,
    // paddingRight: '5px',
    sortable: false,
    label: '',
  },
  {
    id: 'TEXT',
    paddingLeft: '0',
    sortable: true,
    label: 'Initiatives',
    width: '33%',
  },
  {
    id: 'PARENT_TASK',
    paddingLeft: '0',
    sortable: false,
    label: 'Parent',
    width: '12%',
  },
  { id: 'TAGS', paddingLeft: '0', sortable: true, label: 'Tags', width: '12%' },
  {
    id: 'TEAM',
    paddingLeft: '0',
    sortable: false,
    label: 'Owners',
    width: '16%',
  },
  // { id: 'TAGS', paddingLeft: '0', sortable: true, label: 'Objective' },
  // { id: 'PROGRESS', paddingLeft: '1.65rem', sortable: false, label: 'Progress' },
  // { id: 'NAME', paddingLeft: '0', paddingRight: '10px', sortable: true, label: 'Team' },
  {
    id: 'ACTIVE_CONTENT',
    paddingLeft: '1.65rem',
    sortable: true,
    label: 'Due On',
    width: '10%',
  },
  // { id: 'STATS', centerAlign: true, sortable: false, label: '' },
  // { id: 'ACTIONS', paddingRight: '2px', label: '' },
  { id: 'DETAILS', label: '', width: '8%' },
  { id: 'BLANK', label: '' },
];

export const TAG_TASKS_LIST_HEADERS = [
  {
    id: 'STATUS',
    centerAlign: true,
    // paddingRight: '5px',
    sortable: false,
    label: '',
  },
  { id: 'TEXT', paddingLeft: '0', sortable: true, label: 'Task Title' },
  {
    id: 'PARENT_TASK',
    paddingLeft: '0',
    sortable: false,
    label: 'Parent Task',
  },
  { id: 'TEAM', paddingLeft: '0', sortable: false, label: '' },
  {
    id: 'NAME',
    paddingLeft: '0',
    paddingRight: '10px',
    sortable: true,
    label: 'Team',
  },
  {
    id: 'ACTIVE_CONTENT',
    paddingLeft: '1.65rem',
    sortable: true,
    label: 'Due On',
  },
  { id: 'STATS', centerAlign: true, sortable: false, label: '' },
  // { id: 'ACTIONS', paddingRight: '2px', label: '' },
  { id: 'DETAILS', label: '' },
];

export const GOALS_LIST_HEADERS = [
  {
    id: 'TEXT',
    paddingLeft: '1.5rem',
    sortable: true,
    label: 'Goal Name',
    width: '35%',
  },
  {
    id: 'TAGS',
    paddingLeft: '1rem',
    sortable: true,
    label: 'Objective',
    width: '15%',
  },
  {
    id: 'NAME',
    paddingLeft: '1rem',
    sortable: true,
    label: 'Team',
    width: '20%',
  },
  {
    id: 'GOAL_PROGRESS_PERCENTAGE',
    paddingLeft: '1.65rem',
    sortable: true,
    label: 'Progress',
    width: '25%',
  },
  { id: 'DETAILS', label: '', width: '5%' },
];

export const TEAM_TYPE = {
  preferred: 'DEFAULT',
  personal: 'PERSONAL',
  hris: 'HRIS',
  other: 'OTHER',
} as const;

export const TASK_TYPES_1 = {
  task: 'TASK',
  recurrentTask: 'RECURRENT_TASK',
  recurrentInstanceTask: 'RECURRENT_INSTANCE_TASK',
  checkIn: 'CHECK_IN',
} as const;

export const TASK_TYPES_2 = {
  task: 'TASK',
  goal: 'GOAL',
  checkIn: 'CHECK_IN',
  agenda: 'AGENDA_ITEM',
} as const;

const DAYS = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

export const MONTHLY_RECURRENCE_TYPES = [
  {
    label: 'First',
    value: 'MONTH_FIRST',
    recurrenceValues: DAYS,
  },
  {
    label: 'Second',
    value: 'MONTH_SECOND',
    recurrenceValues: DAYS,
  },
  {
    label: 'Third',
    value: 'MONTH_THIRD',
    recurrenceValues: DAYS,
  },
  {
    label: 'Last',
    value: 'MONTH_LAST',
    recurrenceValues: DAYS,
  },
];

export const RECURRENCE_TYPES = [
  {
    label: 'None',
    value: 'NONE',
    recurrenceValues: [],
  },
  {
    label: 'Daily',
    value: 'CUSTOM',
    recurrenceValues: ['DAILY'],
  },
  {
    label: 'Weekly',
    value: 'DAY',
    recurrenceValues: DAYS,
  },
  {
    label: 'Monthly',
    value: 'MONTHLY',
    recurrenceValues: DAYS,
  },
];

export const RECURRENT_SUB_TASK_TYPES = {
  self: 'SELF',
  oneonone: 'ONEONONE',
} as const;

export const REPEATING_TASK_TEXT = 'This is a repeating task';

export const TEAM_VISIBILITY = {
  global: 'GLOBAL',
  private: 'PRIVATE',
} as const;

export const LANDING_PAGE_TYPES = {
  team: 'TEAM',
  task: 'TASK',
  tag: 'HASHTAG',
} as const;

export const GOAL_MEASUREMENT_TYPES = [
  {
    symbol: null,
    name: '',
    code: 'CURRENCY',
    isPrefix: true,
  },
  {
    symbol: '%',
    name: 'Percentage',
    code: 'PERCENTAGE',
    isPrefix: false,
  },
  {
    symbol: '#',
    name: 'Count',
    code: 'COUNT',
    isPrefix: false,
  },
];

export const TASK_STATUS = {
  ongoing: {
    label: 'Ongoing',
    value: 'ONGOING',
    color: 'orange',
  },
  delayed: {
    label: 'Delayed',
    value: 'DELAYED',
    color: 'red',
  },
  unplanned: {
    label: 'Unplanned',
    value: 'UNPLANNED',
    color: 'blue',
  },
  completed: {
    label: 'Completed',
    value: 'COMPLETED',
    color: 'green',
  },
};

export const TASK_DETAILED_STATUS = {
  delayed: 'DELAYED',
  ongoing: 'ONGOING',
  completedWithDelay: 'COMPLETED_WITH_DELAY',
  completedOnTime: 'COMPLETED_ON_TIME',
} as const;

export const TENOR_ENDPOINT = `${process.env.PUBLIC_APP_HTTPS === 'true' ? 'https' : 'http'}://api.tenor.com/v1/`;

export const PLAN_TYPE = {
  enterprise: 'ENTERPRISE',
} as const;

export const TAG_ID_TYPE = {
  subTask: 'SUBTASK',
  tagged: 'TAGGED;',
  explicit: 'EXPLICIT',
} as const;

export const TASK_OPTION_ID = {
  goal: -4,
  checkIn: -5,
  all: -1,
  personal: -2, // TODO: confirm that personal is -2 only
} as const;

export const MARKETING_SITE_CONTACT_US_ROUTE = 'https://www.mesh.ai/contact-us';

export const MARKETING_SITE = 'https://www.mesh.ai/';

export const MESH_TAG_TYPES = [
  {
    value: 'MESH_TASK',
    label: 'Initiative Tags',
    singularLabel: 'Task Tag',
    tagLabel: 'Tag',
    isEnterpriseAccessOnly: false,
  },
  {
    value: 'GOAL',
    label: 'Objectives',
    singularLabel: 'Objective',
    tagLabel: 'Objective',
    isEnterpriseAccessOnly: false,
  },
  {
    value: 'CORE_COMPETENCIES',
    label: 'Competencies',
    singularLabel: 'Competency',
    tagLabel: 'Name',
    isEnterpriseAccessOnly: true,
  },
  {
    value: 'ENGAGEMENT_DRIVER',
    label: 'Drivers',
    singularLabel: 'Driver',
    tagLabel: 'Driver',
    isEnterpriseAccessOnly: true,
  },
];

export const NOTIFICATION_POLL_INTERVAL = 1000 * 6 * 5;

export const LOGIN_TYPES = {
  mesh: 'MESH',
  saml: 'SAML',
  google: 'GOOGLE',
  jira: 'JIRA',
} as const;

export const EMPLOYEE_NAME_REGEX = /^[^<>"']*$/;

export const CHECK_IN_COMMENT_PLACEHOLDER = 'Share comments on talking point here';
export const CHECK_IN_MEETING_NOTES_PLACEHOLDER = 'Share meeting notes here';

export const sampleRosterFileURI = 'https://media.mesh.ai/mesh/media/MeshBulkUploadSample-4.csv';

export const sampleObjectiveFileCompactURI =
  'https://meshai-media.s3.ap-south-1.amazonaws.com/mesh/media/bulk_upload_templates/objective_upload_template+-+COMPACT.csv';
export const sampleObjectiveFileDetailedURI =
  'https://meshai-media.s3.ap-south-1.amazonaws.com/mesh/media/bulk_upload_templates/objective_upload_template+-+DETAILED.csv';
export const sampleObjectiveFileSimpleURI =
  'https://meshai-media.s3.ap-south-1.amazonaws.com/mesh/media/bulk_upload_templates/objective_upload_template+-+SIMPLE.csv';

export const sampleNewGoalFileURI =
  'https://meshai-media.s3.ap-south-1.amazonaws.com/mesh/media/bulk_upload_templates/goal_upload_template+-+KR.csv';
export const sampleObjectiveFileURI = 'https://media.mesh.ai/mesh/media/ObjectiveUploadSample.csv';
export const sampleGoalFileURI = 'https://media.mesh.ai/mesh/media/OkrUploadSample.csv';
export const sampleCompetencyFileURI = 'https://media.mesh.ai/mesh/media/CompetencyUploadSample.csv';
export const sampleCompetencyFunctionAndRoleFileURI =
  'https://media.mesh.ai/mesh/media/FunctionAndRoleSpecificCompetencyUpload.csv';

export const sampleEmployeeSelectionFileURI = 'https://media.mesh.ai/mesh/media/EmployeeCSVSample.csv';

// for use in netchex
export const sampleEmployeeCodeSelectionFileURI =
  'https://media.performance.netchexonline.net/mesh/media/compensation/EmployeeListSample.csv';

export const NoPodManagerErrorText = (teamName) =>
  `Please request admin to assign manager role to one of the members in ${teamName} pod.`;

export const userguideJSONURI = 'https://media.mesh.ai/mesh/media/userguide.json';
export const troubleshootJSONURI = 'https://media.mesh.ai/mesh/media/troubleshoot.json';

export const demoVideoURI = 'https://media.mesh.ai/ProductDemo.mp4';

export const CHECKINS_FREQUENCY_OPTIONS = [
  {
    label: 'Weekly',
    value: 'WEEKLY',
  },
  // {
  //   label: 'Fortnightly',
  //   value: 'FORTNIGHTLY'
  // },
  {
    label: 'Monthly',
    value: 'MONTHLY',
  },
];

export const APPLAUSES_FREQUENCY = ['MONTHLY', 'QUARTERLY', 'YEARLY'];

export const APPLAUSE_AMOUNT_HELP_TEXT =
  "At the chosen frequency, every employee's wallet balance will be reset to the following amount";

export const TASK_TYPE_FILTER_OPTIONS = [
  // {
  //   value: 'TASK',
  //   label: 'Task'
  // },
  {
    value: 'SUB_TASK',
    label: 'Task',
  },
];

export const SELECTED_QUESTION_PILLS = {
  ACTIVE: 'active',
  ARCHIVED: 'archived',
  SAMPLE: 'sample',
} as const;

export const REVIEW_FORMS_TYPE_MAP = {
  COMPETENCY: 'COMPETENCY',
  QNA: 'QNA',
  GOAL: 'GOAL',
  PEER_NOMINATION: 'PEER_NOMINATION',
} as const;

export const REVIEW_STAGES = {
  DRAFT: 'DRAFT',
  PUBLISHED: 'PUBLISHED',
} as const;

// todo: remove this one after cleanup. use the REVIEW_RESPONSE_PROVIDER_TYPE_TITLE_MAP_1 and append 'Review' whereever required
export const getReviewResponseProviderTypeTitleMap = () => {
  return {
    SELF: 'Self Review',
    MANAGER: `${getReviewerTerm()} Review`,
    PEER: 'Peer Review',
    UPWARD: 'Upward Review',
    NOMINATOR: 'Nomination',
    DLM: `${getAdditionalReviewerTerm()} Review`,
  } as const;
};

export const getReviewResponseProviderTypeTitleMap1 = () => {
  return {
    SELF: 'Self',
    MANAGER: getReviewerTerm(),
    PEER: 'Peer',
    UPWARD: 'Upward',
    NOMINATOR: 'Nomination',
    DLM: getAdditionalReviewerTerm(),
  } as const;
};

const palette = theme.palette as unknown as {
  [key: string]: { main: string; light: string };
};

export const REVIEW_SUBMISSION_STATUS_MAP = {
  NOT_STARTED: {
    actionText: 'Start',
    statusText: 'Not Started',
    textColor: palette.red.main,
    bgColor: palette.red.light,
    Icon: Play,
    infoChipColor: 'black',
  },
  IN_PROGRESS: {
    actionText: 'Continue',
    statusText: 'In progress',
    textColor: palette.orange.main,
    bgColor: palette.orange.light,
    Icon: Continue,
    infoChipColor: 'yellow',
  },
  SUBMITTED: {
    actionText: 'View Result',
    statusText: 'Completed',
    textColor: palette.green.main,
    bgColor: palette.green.light,
    infoChipColor: 'green',
  },
  ACKNOWLEDGED: {
    actionText: 'View Result',
    statusText: 'Acknowledged',
    textColor: palette.green.main,
    bgColor: palette.green.light,
    infoChipColor: 'green',
  },
  PUBLISHED: {
    actionText: 'Published',
    statusText: 'Published',
    textColor: palette.green.main,
    bgColor: palette.green.light,
    infoChipColor: 'darkGreen',
  },
};

export const ANALYTICAL_QUESTION_TYPE = {
  OUTCOME: 'OUTCOME',
} as const;

export const QUESTION_FORM_TYPE = {
  REVIEW: 'REVIEW',
} as const;

export const REVIEW_ANSWER_TYPE_MAP = {
  DROP_DOWN: 'DROP_DOWN',
  NONE: 'NONE',
  MULTI_SELECT: 'MULTI_SELECT',
  STAR_RATING: 'STAR_RATING',
  NUMBER_RATING: 'NUMBER_RATING',
  COMMENT: 'COMMENT',
  EMOJI: 'EMOJI',
  SHORT_TEXT: 'SHORT_TEXT',
} as const;

export const QUESTION_TYPE = {
  QNA: 'QNA',
  GOAL: 'GOAL',
  COMPETENCY: 'COMPETENCY',
  ENGAGEMENT: 'ENGAGEMENT',
  EMOTION: 'EMOTION',
  TALKING_POINT: 'TALKING_POINT',
} as const;

export const ACTIONS = {
  CREATE: 'create',
  EDIT: 'edit',
  COPY: 'copy',
} as const;

/**
 * Helper to check if a question type is one of the review-related types (QNA, GOAL, COMPETENCY)
 */
export const isReviewQuestionType = (questionType: string): boolean =>
  ([QUESTION_TYPE.QNA, QUESTION_TYPE.GOAL, QUESTION_TYPE.COMPETENCY] as string[]).includes(questionType);

export const REVIEW_FORM_COMPLETION_STATUS = {
  NOT_STARTED: 'NOT_STARTED',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  SUBMITTED: 'SUBMITTED',
  ACKNOWLEDGED: 'ACKNOWLEDGED',
  PUBLISHED: 'PUBLISHED',
} as const;

export const REVIEW_TEXTS = {
  GOALS_NOT_ENABLED: {
    LINE1: 'This feature is not yet activated for your organization and will be used in future review cycles.',
    LINE2: 'Please request your admin to enable this feature',
  },
  NO_GOALS_TEXT: {
    LINE1: 'You currently do not have any goals/OKRs created on Mesh.',
    LINE2: 'Please connect with your manager to discuss and create your goals/OKRs for the next cycle.',
  },
  NO_STRENGTHS_TEXT: {
    LINE1: 'You currently do not have any strengths created on Mesh.',
    LINE2: 'Please connect with your manager to discuss and create your strengths for the next cycle.',
  },
  GOALS: {
    NO_DATA: 'No Goals to display.',
    ERROR_MSG: 'Some error occurred while fetching goals.',
  },
  CORE_COMPETENCIES: {
    NO_DATA: 'No Core competencies to display.',
    ERROR_MSG: 'Some error occurred while fetching core competencies.',
  },
  QA: {
    NO_DATA: 'No Questions to display.',
    ERROR_MSG: 'Some error occurred while fetching questions.',
  },
  LANDING_PAGE_REVIEWS_TO_COMPLETE: {
    NO_DATA: 'No on-going reviews.',
    ERROR_MSG: 'Request to fetch on-going reviews failed!',
  },
  LANDING_PAGE_ARCHIVED_REVIEWS: {
    NO_DATA: 'No completed reviews.',
    ERROR_MSG: 'Request to fetch completed reviews failed!',
  },
  LANDING_PAGE_TEAM_STATUS: {
    NO_DATA: 'No Data to display',
    ERROR_MSG: 'Request to fetch Team status failed!',
  },
  REVIEW_PROVIDER_HEADER: {
    NO_DATA: 'No Data to display',
    ERROR_MSG: 'Request to fetch Review Providers list failed!',
  },
};

export const ANALYTICS_TEXTS = {
  REVIEW_TABLE: {
    NO_DATA: 'No Data to display',
    ERROR_MSG: 'Request to fetch data failed. Please try again later.',
  },
  REVIEW: {
    CALIBRATION: {
      NO_DATA: 'No Data to display',
      ERROR_MSG: 'Request to fetch data failed. Please try again later.',
    },
  },
  COHORT: {
    NO_DATA:
      'There isn’t any information for the selected cohort/employee. \n You can go back or change time period or update selected cohort.',
  },
};

export const SLACK_PRIVATE_CHANNEL_INFO_TEXT =
  "To get private channels, open 'Channel details' tab, choose 'More' in dropdown menu, choose 'Add apps' and add 'Mesh' app";

// this is to be depreciated. Don't use this going forward. Instead use following maps.
export const ORG_ID = {
  1064: 'mesh',
  1194: 'meshDemo',
  1722: 'meshDemo2',
  1283: 'sharechat',
  1259: 'cred',
  1733: 'cred2',
  1692: 'pipefy',
  2020: 'kitabisa',
  2075: 'zeta.tech',
} as const;

export const ORG_ID_TO_NAME_MAP = ORG_ID;

export const ORG_NAME_TO_ID_MAP = Object.entries(ORG_ID_TO_NAME_MAP).reduce((mappedObj, [id, name]) => {
  // biome-ignore lint/style/noParameterAssign: No explanation provided in original eslint-disable
  mappedObj[name] = id;
  return mappedObj;
}, {});

export const TASK_SHORT_STATUS = {
  toBeDone: {
    label: 'To be done',
    color: 'orange',
    key: 'ACTIVE',
  },
  completed: {
    label: 'Completed',
    color: 'green',
    key: 'COMPLETED',
  },
};

export const MANAGER_DELIVERABLE_OPTIONS = [
  {
    value: 'SHARE',
    label: 'Share the review and scores with their team member',
  },
  {
    value: 'SHARE_TEXT_ONLY',
    label: 'Share only the review with the team member',
  },
  {
    value: 'DONT_SHARE',
    label: "Don't share the review with their team member",
  },
];

export const PEER_DELIVERABLE_OPTIONS = [
  {
    value: 'SHARE',
    label: 'Share the review with the reviewers name',
  },
  {
    value: 'ANONYMIZE',
    label: 'Share the review anonymously',
  },
  {
    value: 'DONT_SHARE',
    label: "Don't share review at all",
  },
];

const JIRA_PLUGIN = {
  value: 'JIRA',
  label: 'Jira',
};

const SALESFORCE_PLUGIN = {
  value: 'SALESFORCE',
  label: 'Salesforce',
};

const DARWINBOX_PLUGIN = {
  value: 'DARWINBOX',
  label: 'Darwinbox',
};

export const TRACKING_OPTIONS = [JIRA_PLUGIN, SALESFORCE_PLUGIN, DARWINBOX_PLUGIN];

export const TRACKING_DISABLE_TOOLTIP =
  'This plugin is not enabled, please contact Mesh Admin to enable this for your organization';

export const TRACKING_OPTIONS_MAP = {
  JIRA_PLUGIN,
  SALESFORCE_PLUGIN,
  DARWINBOX_PLUGIN,
} as const;

export const GOAL_PROGRESS_TYPE_OPTIONS = {
  MAX: {
    heading: 'Maximum Progress',
    subHeading: 'Goals with most progress so far',
  },
  MIN: {
    heading: 'Minimum Progress',
    subHeading: 'Goals with least progress so far',
  },
};

export const GOAL_PROGRESS_CHART_COLORS = {
  progress: palette.primary.main,
  checkIns: '#c0c0c0',
} as const;

export const VISIBLE_PROVIDER_TYPES_COLOR_MAPPING = {
  SELF: 'orange',
  MANAGER: 'darkGreen',
  PEER: 'blue',
  UPWARD: 'red',
  DLM: 'purpleChart',
} as const;

// For Performance Review Graph on the Review page.
export const REVIEW_CYCLE_RATING_SPLIT_COLOR_MAPPING = {
  PERFORMANCE: 'darkGreen',
  OVERALL: 'darkGreen',
  POTENTIAL: 'darkGreen',
  QNA: 'darkGreen',
} as const;

// Can be of many stage types apart from PUBLISHED
export const REVIEW_INFO_FEEDBACK_FORM_CURRENT_STAGE = {
  PUBLISHED: 'PUBLISHED',
} as const;

export const REVIEW_CYCLE_STEPS = [
  'NAME',
  'REVIEWEES',
  'REVIEWERS',
  'RATING_SETTINGS',
  'REVIEW_SETTINGS',
  'DELIVERABLES',
  'TIMELINES',
  'REMINDERS',
  'VALIDATION',
  'SUMMARY',
];

export const PULSE_KICKSTART_STEPS = [
  'SURVEY_NAME',
  'SURVEY_PARTICIPANTS',
  'SURVEY_TEMPLATE',
  'SURVEY_SETTINGS',
  'SURVEY_REMINDERS',
  'SURVEY_VALIDATION',
  'SURVEY_SUMMARY',
];

export const FEEDBACK_FORM_TYPE = {
  PROVIDED: 'PROVIDED',
  REQUESTED: 'REQUESTED',
  PROVIDING_REQUESTED: 'PROVIDING_REQUESTED',
  SHOW_PROVIDED: 'SHOW_PROVIDED',
  SHOW_REQUESTED: 'SHOW_REQUESTED',
  SHOW_RECEIVED: 'SHOW_RECEIVED',
  LIST_FEEDBACKS: 'LIST_FEEDBACKS',
} as const;

export const FEEDBACK_FORM_VISIBILITY = [
  {
    value: 'PUBLIC',
    label: 'Public',
    icon: <Public />,
  },
  {
    value: 'RECEIVER_AND_MANAGER',
    label: 'Receiver and manager',
    icon: <ReceiverManager />,
  },

  {
    value: 'RECEIVER',
    label: 'Only receiver',
    icon: <Person />,
  },

  {
    value: 'MANAGER',
    label: 'Only manager ',
    icon: <ManagerRabbit />,
  },
  {
    value: 'NONE',
    label: 'Note to self',
    icon: <BulletList />,
  },
];

export const FEEDBACK_FORM_VISIBILITY_RABBIT = [
  {
    value: 'PUBLIC',
    label: 'Public',
    icon: 'PublicViewIcon',
  },
  {
    value: 'RECEIVER_AND_MANAGER',
    label: 'Receiver and manager',
    icon: 'PrivateManagerViewIcon',
  },

  {
    value: 'RECEIVER',
    label: 'Only receiver',
    icon: 'PrivateViewIcon',
  },

  {
    value: 'MANAGER',
    label: 'Only manager ',
    icon: 'ManagerViewIcon',
  },
  {
    value: 'NONE',
    label: 'Note to self',
    icon: 'Details',
  },
];

export const FEEDBACK_FORM_VISIBILITY_RABBIT_WITHOUT_PUBLIC = [
  {
    value: 'RECEIVER_AND_MANAGER',
    label: 'Receiver and manager',
    icon: 'PrivateManagerViewIcon',
  },

  {
    value: 'RECEIVER',
    label: 'Only receiver',
    icon: 'PrivateViewIcon',
  },

  {
    value: 'MANAGER',
    label: 'Only manager ',
    icon: 'ManagerViewIcon',
  },
  {
    value: 'NONE',
    label: 'Note to self',
    icon: 'Details',
  },
];

export const SHARE_FEEDBACK_TYPES = [
  {
    value: SENTIMENTS.appreciative,
    label: 'Praise',
    icon: 'PositiveSentiment',
  },
  {
    value: SENTIMENTS.constructive,
    label: 'Feedback',
    icon: 'NegativeSentiment',
  },
  // {
  //   value: SENTIMENTS.neutral,
  //   label: 'Neutral',
  //   icon: 'NeutralSentiment'
  // }
];

export const SHARE_FEEDBACK_TYPES_V2 = [
  {
    value: SENTIMENTS.appreciative,
    label: 'Praise',
    icon: (props) => <Praise fill="transparent" {...props} />,
  },
  {
    value: SENTIMENTS.constructive,
    label: 'Feedback',
    icon: (props) => <Advice fill="transparent" {...props} />,
  },
];

export const SHARE_FEEDBACK_TYPES_FOR_AUTOPOPULATE = [
  {
    value: SENTIMENTS.appreciative,
    label: 'Praise',
    icon: 'PositiveSentiment',
  },
  {
    value: SENTIMENTS.constructive,
    label: 'Feedback',
    icon: 'NegativeSentiment',
  },
];

export const SENTIMENTS_TYPES = {
  appreciative: {
    value: SENTIMENTS.appreciative,
    label: 'Appreciative',
  },
  constructive: {
    value: SENTIMENTS.constructive,
    label: 'Constructive',
  },
  neutral: {
    value: SENTIMENTS.neutral,
    label: 'Neutral',
  },
};

export const SENTIMENT_TOGGLE_ORDER = [SENTIMENTS.neutral, SENTIMENTS.appreciative, SENTIMENTS.constructive];

export const CONTINUOUS_FEEDBACK_FORM_STATUS = {
  NOT_STARTED: 'NOT_STARTED',
  COMPLETED: 'SUBMITTED',
} as const;

export const CONTINUOUS_FEEDBACK_PEER_STATUS = {
  ACCEPTED: 'ACCEPTED',
  DECLINED: 'DECLINED',
  NOMINATED: 'NOMINATED',
  SUBMITTED: 'SUBMITTED',
} as const;

export const REVIEW_PEER_STATUS = {
  ACCEPTED: 'ACCEPTED',
  DECLINED: 'DECLINED',
  NOMINATED: 'NOMINATED',
} as const;

export const NOMINATION_PANEL_CLOSING_STATUS = {
  CANCELLED: 'CANCELLED',
  SUCCESSFUL: 'SUCCESSFUL',
} as const;

export const CONTINUOUS_FEEDBACK_RELOAD_TYPE = {
  ARCHIVED: 'ARCHIVED',
  SHARED_OWN: 'SHARED_OWN',
  PROVIDED_REQUESTED: 'PROVIDED_REQUESTED',
} as const;

export const FEEDBACK_COMMENT_STRINGIFIED_RAW_STATE =
  '{"blocks":[{"key":"9hun0","text":"Continuous Feedback*","type":"unstyled","depth":0,"inlineStyleRanges":[],"entityRanges":[],"data":{}}],"entityMap":{}}';

export const MOST_LEAST_ACTIVE_MANAGER_META = {
  MOST: {
    heading: 'Most active managers',
    subHeading: 'These are managers with most 1:1s every x days',
  },
  LEAST: {
    heading: 'Least active managers',
    subHeading: 'These are managers with least 1:1s every x days',
  },
};

export const MOST_LEAST_ACTIVE_EMPLOYEE_META = {
  MOST: {
    heading: 'Top Performers',
    subHeading: 'They are the most high performing in your organization',
  },
  LEAST: {
    heading: 'Top Laggards',
    subHeading: 'They are the most inactive in your organization',
  },
};

export const MOST_LEAST_ACTIVE_REPORTEE_META = {
  MOST: {
    heading: 'Most Active Team Member',
    subHeading: 'These are your most active team members',
  },
  LEAST: {
    heading: 'Most Inactive Team Members',
    subHeading: 'These are your most inactive team members',
  },
};

export const GOAL_ANALYTICS_VIEW_IDS = {
  OBJECTIVE: 'objective',
  INDIVIDUAL: 'employeeId',
} as const;

export const FEEDBACK_INSIGHTS_VIEW_IDS = {
  CORE_VALUES: 'competency',
  INDIVIDUAL: 'employeeId',
} as const;
export const ALERT_MESSAGES = {
  CONTINUOUS_FEEDBACK: {
    SHARE: {
      SUCCESS: 'Feedback shared successfully!',
      FAILURE: 'An error occurred while sharing the feedback!',
    },
    REQUEST: {
      SUCCESS: 'Feedback request sent',
      FAILURE: 'An error occurred while sending the feedback request!',
    },
    ARCHIVE: {
      SUCCESS: 'Feedback archived',
      FAILURE: 'An error occurred while archiving the feedback!',
    },
    COMMENT: {
      EMPTY: 'Please comment something!',
    },
  },
  GENERIC: {
    FAILURE: 'Oops! something went wrong!',
    SUCCESS: 'Process succeeded',
  },
};

export const FEEDBACK_STRENGTH_TYPES = {
  APPRECIATIVE: SHARE_FEEDBACK_TYPES[0].label,
  CONSTRUCTIVE: SHARE_FEEDBACK_TYPES[1].label,
} as const;

export const ANSWER_TYPES = {
  review: [
    {
      label: 'Score',
      value: 'STAR_RATING',
    },
    {
      label: 'Scale',
      value: 'NUMBER_RATING',
    },
    {
      label: 'Options',
      value: 'DROP_DOWN',
    },
    {
      label: 'Text',
      value: 'NONE',
    },
  ],
  engagement: [
    {
      label: 'Emoji',
      value: 'EMOJI',
    },
    {
      label: 'Number',
      value: 'NUMBER_RATING',
    },
    {
      label: 'Options',
      value: 'DROP_DOWN',
    },
    {
      label: 'Text',
      value: 'NONE',
    },
  ],
  settings: [
    {
      label: 'Options',
      value: 'DROP_DOWN',
      isDisabled: false,
    },
    {
      label: 'Numeric',
      value: 'NUMBER_RATING',
      isDisabled: true,
    },
  ],
  one_on_one: [
    {
      label: 'Comments',
      value: 'COMMENT',
    },
    {
      label: 'Emoji',
      value: 'EMOJI',
    },
  ],
};

export const QUESTION_TYPES = {
  review: [
    {
      label: 'Goal',
      value: 'GOAL',
    },

    {
      label: 'Generic',
      value: 'QNA',
    },
    {
      label: 'Competency',
      value: 'COMPETENCY',
    },
  ],
  engagement: [],
  one_on_one: [
    { label: 'Emotion', value: 'EMOTION' },
    {
      label: 'Talking Point',
      value: 'TALKING_POINT',
    },
  ],
};

export const GENDER_MAPPING = {
  MALE: 'Male',
  FEMALE: 'Female',
} as const;

export const QUESTION_FORM_SOURCES = {
  ENGAGEMENT: 'engagement',
  REVIEW: 'review',
  ONE_ON_ONE: 'one_on_one',
} as const;

export const QUESTION_MENU_ACTION = {
  DUPLICATE: 'DUPLICATE',
  COPY_LINK: 'COPY_LINK',
  ARCHIVE: 'ARCHIVE',
  UNARCHIVE: 'UNARCHIVE',
} as const;

// Question Template

export const QUESTION_MENU_OPTIONS: Record<SelectedQuestionPills, QuestionMenuAction[]> = {
  active: [QUESTION_MENU_ACTION.DUPLICATE, QUESTION_MENU_ACTION.COPY_LINK, QUESTION_MENU_ACTION.ARCHIVE],
  sample: [QUESTION_MENU_ACTION.DUPLICATE],
  archived: [QUESTION_MENU_ACTION.DUPLICATE, QUESTION_MENU_ACTION.COPY_LINK, QUESTION_MENU_ACTION.UNARCHIVE],
};

// Review Template
export const REVIEW_TEMPLATE_MENU_OPTIONS = {
  active: [QUESTION_MENU_ACTION.DUPLICATE, QUESTION_MENU_ACTION.ARCHIVE],
  archived: [QUESTION_MENU_ACTION.DUPLICATE, QUESTION_MENU_ACTION.UNARCHIVE],
} as const;

/* Date Range options */
export const yesterday = {
  label: 'Yesterday',
  startDate: subDays(startOfDay(new Date()), 1),
  endDate: subDays(endOfDay(new Date()), 1),
};

export const lastWeek = {
  label: 'Last Week',
  startDate: subWeeks(startOfWeek(new Date()), 1),
  endDate: subWeeks(endOfWeek(new Date()), 1),
};

export const lastTwoWeeks = {
  label: 'Last 2 Weeks',
  startDate: subWeeks(startOfWeek(new Date()), 2),
  endDate: subWeeks(endOfWeek(new Date()), 1),
};

export const thisQuarter = {
  label: 'This Quarter',
  startDate: startOfQuarter(new Date()),
  endDate: endOfQuarter(new Date()),
};

export const upcomingQuarter = {
  label: 'Upcoming Quarter',
  startDate: startOfQuarter(addQuarters(new Date(), 1)),
  endDate: endOfQuarter(addQuarters(new Date(), 1)),
};

export const lastQuarter = {
  label: 'Last Quarter',
  startDate: subMonths(startOfQuarter(new Date()), 3),
  endDate: subSeconds(startOfQuarter(new Date()), 1),
};

export const lastTwoQuarters = {
  label: 'Last 2 Quarters',
  startDate: subMonths(startOfQuarter(new Date()), 6),
  endDate: subSeconds(startOfQuarter(new Date()), 1),
};

export const thisMonth = {
  label: 'This Month',
  startDate: startOfMonth(new Date()),
  endDate: endOfMonth(new Date()),
};

export const upcomingMonth = {
  label: 'Upcoming Month',
  startDate: startOfMonth(addMonths(new Date(), 1)),
  endDate: endOfMonth(addMonths(new Date(), 1)),
};

export const upcomingWeek = {
  label: 'Upcoming Week',
  startDate: startOfWeek(addWeeks(new Date(), 1)),
  endDate: endOfWeek(addWeeks(new Date(), 1)),
};

export const lastMonth = {
  label: 'Last Month',
  startDate: subMonths(startOfMonth(new Date()), 1),
  endDate: subSeconds(startOfMonth(new Date()), 1),
};

export const lastTwoMonths = {
  label: 'Last 2 Months',
  startDate: subMonths(startOfMonth(new Date()), 2),
  endDate: subSeconds(startOfMonth(new Date()), 1),
};

export const thisYear = {
  label: 'This Year',
  startDate: startOfYear(new Date()),
  endDate: endOfYear(new Date()),
};

export const past6Months = {
  label: 'Past 6 Months',
  startDate: subMonths(startOfDay(new Date()), 6),
  endDate: subSeconds(endOfDay(new Date()), 0),
};

export const past12Months = {
  label: 'Past 12 months',
  startDate: subYears(startOfDay(new Date()), 1),
  endDate: subSeconds(endOfDay(new Date()), 0),
};

export const past3Months = {
  label: 'Past 3 Months',
  startDate: subMonths(startOfDay(new Date()), 3),
  endDate: subSeconds(endOfDay(new Date()), 0),
};

export const ANALYTICS_PREDEFINED_DATERANGES = [
  thisQuarter,
  lastQuarter,
  lastTwoQuarters,
  thisMonth,
  lastMonth,
  lastTwoMonths,
  thisYear,
  past3Months,
];

export const TEAMHUB_PREDEFINED_DATERANGES = ANALYTICS_PREDEFINED_DATERANGES;

export const TEAMHUB_LAST_ACTIVITY_PREDEFINED_DATERANGES = [yesterday, lastWeek, lastTwoWeeks, lastMonth];

export const TEAMHUB_LAST_ACTIVITY_DATERANGE_ENUM_MAP = {
  [yesterday.label]: TEAM_DURATIONS_OBJECT.yesterday,
  [lastWeek.label]: TEAM_DURATIONS_OBJECT.lastWeek,
  [lastTwoWeeks.label]: TEAM_DURATIONS_OBJECT.last2Weeks,
  [lastMonth.label]: TEAM_DURATIONS_OBJECT.lastMonth,
} as const;

export const PULSE_SURVEY_CREATE_STATUS = {
  DRAFT: 'DRAFT',
  COMPLETED: 'PUBLISHED',
  ACTIVE: 'IN_PROGRESS',
} as const;

/* Feedback Analytics */
export const MOST_LEAST_GROWTH_COMPETENCY_META = {
  MOST: {
    heading: 'Maximum Growth',
    subHeading: 'There is maximum growth in these competencies',
  },
  LEAST: {
    heading: 'Minimum Growth',
    subHeading: 'There is minimum growth in these competencies',
  },
};

export const HIGHEST_LOWEST_EMPLOYEE_STANDING_META = {
  MOST: {
    heading: 'Highest Rated',
    subHeading: 'These employees have highest comptency growth',
  },
  LEAST: {
    heading: 'Lowest Rated',
    subHeading: 'These employees have lowest comptency growth',
  },
};

/* Engagement Analytics */
export const HIGHEST_LOWEST_RATED_COHORT_META = {
  MOST: {
    heading: 'Most engaged cohort(s)',
    subHeading: 'These groups are the most engaged',
  },
  LEAST: {
    heading: 'Least engaged cohort(s)',
    subHeading: 'These groups are the least engaged',
  },
};

export const REVIEW_PROVIDER_TYPES = {
  MANAGER: 'MANAGER',
  SELF: 'SELF',
  PEER: 'PEER',
  UPWARD: 'UPWARD',
  DLM: 'DLM',
  //   EXTERNAL: 'EXTERNAL', // until supported
} as const;

// MeshList consts
export const MESH_LIST_TABS = {
  task: {
    id: -1,
    taskType: TASK_TYPES_2.task,
    displayName: 'Initiatives',
  },
  goal: {
    id: -4,
    taskType: TASK_TYPES_2.goal,
    displayName: 'Goals',
  },
  // checkin should be renamed to oneOnOne
  checkIn: {
    id: -5,
    taskType: TASK_TYPES_2.checkIn,
    displayName: '1:1',
  },
  feedback: {
    id: -6,
    taskType: 'FEEDBACK',
    displayName: 'Feedback',
  },
  review: {
    id: -42, // this is random number and only used in local storage to match
    taskType: 'REVIEW',
    displayName: 'Reviews',
  },
  levelUp: {
    id: -44, // this is random number and only used in local storage to match
    taskType: 'LEVEL_UP',
    displayName: 'Competencies',
  },
  competencies: {
    id: -46, // this is random number and only used in local storage to match
    taskType: 'COMPETENCY',
    displayName: 'Competencies',
  },
};

export const PEER_NOMINATION_COHORTS = {
  JUNIORS: 'JUNIORS',
  SENIORS: 'SENIORS',
  PRIMARY_TEAM: 'PRIMARY_TEAM',
  OUTSIDE_TEAM: 'OUTSIDE_TEAM',
  ALL_ORG: 'ALL_ORG',
  DLM: 'DLM',
} as const;

export const COHORTS_ID_MAPPING = {
  ALL_EMPLOYEE: '-1',
  MY_TEAM: '-2',
  MY_ORGANIZATION: '-3',
  CUSTOM_TYPE: '',
  POD: '',
  CUSTOM: '',
  OUTSIDE_TEAM: '-5',
  PRIMARY_TEAM: '-6',
  SENIORS: '-7',
  JUNIORS: '-8',
  MY_ONE_ON_ONE: '-9',
  MY_DEPARTMENT: '-10',
  MY_INITIATIVES: '-11',
  MY_OKRs: '-12',
  MY_DATA: '-13',
} as const;

export const DEFAULT_ROWS_PER_PAGE = 25;

export const ANALYTICS_FILTERS_KEYS = {
  cohortId: 'cohortId',
  groupByKey: 'groupByKey',
  dateRange: 'dateRange',
  sourceUuid: 'sourceUuid',
  reviewViewType: 'reviewViewType',
  splitByKey: 'splitByKey',
  timeBounds: 'timeBounds',
} as const;
export const GOAL_PROGRESS_ENTITIES = {
  ACHIEVEDLIST: 'achievedList',
  TARGETLIST: 'targetList',
} as const;

export const MISCELLANEOUS_COMPETENCY_NAME = 'Miscellaneous';
export const MISCELLANEOUS_COMPETENCY_ID = 'MISC';
export const VIEW_ALL_COMPETENCY_ID = null;

export const ONEONONE_ANALYTICS_CHART_CONSTANTS = {
  activityGraph: {
    yAxisLabel: {
      employeeId: 'Number of team members',
      manager: 'Number of managers',
    },
    xAxisLabel: {
      employeeId: 'Percentage of completed 1:1s',
      manager: 'Percentage of completed 1:1s',
    },
  },
  mostAndLeastActiveChart: {
    employeeId: {
      MOST: {
        heading: 'Most Frequent 1:1s',
        subHeading: 'These are team members with most 1:1s every x days',
      },
      LEAST: {
        heading: 'Least Frequent 1:1s',
        subHeading: 'These are team members with least 1:1s every x days',
      },
    },
    manager: {
      MOST: {
        heading: 'Most Active Managers',
        subHeading: 'These are managers with most 1:1s every x days',
      },
      LEAST: {
        heading: 'Least Active Managers',
        subHeading: 'These are managers with least 1:1s every x days',
      },
    },
  },
};
export const RATINGS_TYPES = {
  OVERALL: 'Overall Performance Score',
  PERFORMANCE: 'Goal score',
  POTENTIAL: 'Competency score',
  QNA: 'Overall questions score',
} as const;

export const ANALYTICS_EMAIL_SUBJECTS = {
  GOALS_TABLE: {
    emailSubject: 'Goal Progress',
  },
  ONEONONE_TABLE: {
    emailSubject: '1:1 meeting',
  },
  FEEDBACK_TABLE: {
    emailSubject: 'Competency Progress',
  },
};

export const CHARTS_PADDING = '0.75rem';
export const ENGAGEMENT_ANSWER_TYPES = {
  EMOJI: 'EMOJI',
  NUMBER_RATING: 'NUMBER_RATING',
  DROP_DOWN: 'DROP_DOWN',
  NONE: 'NONE',
} as const;

export const NUDGE_CARD_TYPES = {
  REVIEW: {
    type: 'REVIEW',
    subTypes: {
      PENDING_SELF: 'PENDING_SELF',
      RESULT_PUBLISHED: 'RESULT_PUBLISHED',
      PENDING_NOMINATION: 'PENDING_NOMINATION',
    },
  },
  ONE_ON_ONE: {
    type: 'ONE_ON_ONE',
    subTypes: {},
  },
  ENGAGEMENT: {
    type: 'ENGAGEMENT',
    subTypes: {
      PENDING_SURVEY: 'PENDING_SURVEY',
    },
  },
  GROWTH: {
    type: 'GROWTH',
    subTypes: {
      APPLAUSE_GENDER_DISPARITY: 'APPLAUSE_GENDER_DISPARITY',
    },
  },
  SHORT_SURVEY: {
    type: 'SHORT_SURVEY',
    subTypes: {
      SHORT_SURVEY: 'SHORT_SURVEY',
    },
  },
  GOAL: {
    type: 'GOAL',
    subTypes: {
      GOAL_NOT_UPDATED: 'GOAL_NOT_UPDATED',
    },
  },
};

export const PULSE_SURVEY_STATUS = {
  SUBMITTED: 'SUBMITTED',
  NOT_STARTED: 'NOT_STARTED',
  IN_PROGRESS: 'IN_PROGRESS',
} as const;

export const ONE_ON_ONE_STATUSES = {
  NOT_STARTED: 'NOT_STARTED',
  CANCELLED: 'CANCELLED',
  ACTIVE: 'ACTIVE',
  DISABLED: 'DISABLED',
  ACKNOWLEDGED: 'ACKNOWLEDGED',
} as const;

export const ONE_ON_ONE_INSTANCE_VIEWS = {
  INSTANCE_DETAILS: 'INSTANCE_DETAILS',
  RATING: 'RATING',
  SUBMISSION_MESSAGE: 'SUBMISSION_MESSAGE',
} as const;

export const ONE_ON_ONE_TIMELINE_VIEWS = {
  TIMELINE_VIEW: 'TIMELINE_VIEW',
  RATING_VIEW: 'RATING_VIEW',
} as const;

export const REVIEW_QUESTION_TYPES = {
  PERFORMANCE: 'PERFORMANCE',
  POTENTIAL: 'POTENTIAL',
  QNA: 'QNA',
} as const;

export const QUESTION_ORDER_ACTIONS = {
  MOVE_UP: 'MOVE_UP',
  MOVE_DOWN: 'MOVE_DOWN',
} as const;

export const REVIEW_QUESTION_TYPE_MAP_1 = {
  GOAL: 'PERFORMANCE',
  COMPETENCY: 'POTENTIAL',
  QNA: 'QNA',
} as const;

export const REVIEW_QUESTION_TYPE_MAP_2 = {
  PERFORMANCE: 'GOAL',
  POTENTIAL: 'COMPETENCY',
  QNA: 'QNA',
  OVERALL: 'OVERALL',
} as const;

export const REVIEW_QUESTION_TYPE_MAP_3 = {
  PERFORMANCE: 'PERFORMANCE',
  POTENTIAL: 'POTENTIAL',
  QNA: 'QNA',
  OVERALL: 'OVERALL',
  OUTCOME: 'OUTCOME',
} as const;

export const CSV_DOWNLOAD_FILE_REQUEST_WAIT_INTERVAL = 10 * 60 * 1000;
export const CSV_DOWNLOAD_API_POLL_INTERVAL = 2500;

export const GENDER_CHART_COLORS = {
  MALE: '#B5D5F8',
  FEMALE: '#F6A9F8',
  OTHER: '#E8C061',
} as const;

export const COMPETENCY_SIDEPANEL_TYPE = {
  COMPETENCY_DETAILED_PAGE: 'COMPETENCY_DETAILED_PAGE',
  HOME_PAGE: 'HOME_PAGE',
} as const;

export const DEFAULT_CHECKIN_VISIBILITY = 'RECEIVER_AND_MANAGER';

export const CHECKIN_QUESTION_VISIBILITY = {
  manager: {
    value: 'MANAGER',
    label: 'Visible to Manager',
  },
  receiver: {
    value: 'RECEIVER',
    label: 'Visible to Receiver',
  },
  both: {
    value: 'RECEIVER_AND_MANAGER',
    label: 'Visible to Both',
  },
};

export const CHECKIN_QUESTION_TYPES = {
  EMOTION: 'EMOTION',
  TALKING_POINT: 'TALKING_POINT',
  MEETING_NOTE: 'MEETING_NOTE',
  AGENDA_ITEM: 'AGENDA_ITEM',
} as const;

export const TEAM_REVIEW_TEXTS = {
  ERROR_MSG: 'No data to display',
} as const;

export const OBJECTIVE_CREATION_PLACEHOLDER_TEXT = {
  LINE1: 'You can find all the objectives you have created in the past, plus',
  LINE2: 'you can also see and use some pre-populated sample objectives.',
} as const;

export const OBJECTIVE_LEVEL_TAGS = [
  { value: 'COMPANY', label: 'Company', bgColor: '#50B7AE', icon: 'CTagIcon' },
  {
    value: 'DEPARTMENT',
    label: 'Department',
    bgColor: '#7074D5',
    icon: 'DTagIcon',
  },
  { value: 'TEAM', label: 'Team', bgColor: '#F2A043', icon: 'TTagIcon' },
  {
    value: 'INDIVIDUAL',
    label: 'Individual',
    bgColor: '#BB70D5',
    icon: 'ITagIcon',
  },
];

export const OBJECTIVE_LEVEL = {
  DEPARTMENT: 'DEPARTMENT',
  COMPANY: 'COMPANY',
  TEAM: 'TEAM',
  INDIVIDUAL: 'INDIVIDUAL',
} as const;

export const ENGAGEMENT_DRIVERS_TAG_SUB_TYPES = {
  ENPS: { id: 'ENPS', displayName: 'eNPS', bgColor: '#50B7AE' },
  MNPS: { id: 'MNPS', displayName: 'mNPS', bgColor: '#BB70D5' },
  OVERALL_ENGAGEMENT: {
    id: 'OVERALL_ENGAGEMENT',
    displayName: 'Overall Engagement',
    bgColor: '#7074D5',
  },
  ENGAGEMENT_DRIVER: {
    id: 'ENGAGEMENT_DRIVER',
    displayName: 'Engagement Driver',
    bgColor: '#F2A043',
  },
};

export const ENGAGEMENT_DRIVERS_TAG_SUB_TYPES_OPTIONS = [
  {
    label: 'eNPS',
    value: 'ENPS',
    bgColor: '#50B7AE',
  },
  {
    label: 'mNPS',
    value: 'MNPS',
    bgColor: '#BB70D5',
  },
  {
    label: 'Overall Engagement',
    value: 'OVERALL_ENGAGEMENT',
    bgColor: '#7074D5',
  },
  {
    label: 'Engagement Driver',
    value: 'ENGAGEMENT_DRIVER',
    bgColor: '#F2A043',
  },
];

export const oneOnOneMoods = [
  {
    key: 5,
    label: 'Extremely happy',
  },
  {
    key: 4,
    label: 'Happy',
  },
  {
    key: 3,
    label: 'Average',
  },
  {
    key: 2,
    label: 'Sad',
  },
  {
    key: 1,
    label: 'Extremely sad',
  },
];
export const HASHTAG_MENTION_REGEX = '[\\w-*]*';
/*
   it's used to allow "*" to be included in string, by default only "-" & "_" are allowed from special characters in mentionplugin of draft.js. Regex is expected to be a string by draft.js @https://github.com/draft-js-plugins/draft-js-plugins/search?q=mentionRegExp
  */

export const MODULE_STATUS = [
  {
    value: 'DISABLED',
    displayName: 'Disabled',
  },
  {
    value: 'BASIC',
    displayName: 'Base',
  },
  {
    value: 'ADVANCED',
    displayName: 'Advanced',
  },
];

export const HELLO_BAR_PAGE_TYPE = {
  REVIEW: 'REVIEW',
  ENGAGEMENT: 'ENGAGEMENT',
} as const;

export const HELLO_BAR_PAGE_TYPE_2 = {
  REVIEW: 'review',
  ENGAGEMENT: 'engagement',
} as const;

export const ACKNOWLEDGEMENT_STATUS = {
  NOT_STARTED: 'NOT_STARTED',
  SUBMITTED: 'SUBMITTED',
} as const;

export const WEB_APP_COOKIE_DOMAIN = '.mesh.ai';

// Engagement Template
export const ENGAGEMENT_TEMPLATE_MENU_OPTIONS = {
  active: ['DUPLICATE', 'ARCHIVE'],
  archived: ['DUPLICATE', 'UNARCHIVE'],
} as const;

// for outlook add-in
export const OFFICE_JS_SCRIPT_URL = 'https://appsforoffice.microsoft.com/lib/1/hosted/office.js';

export const APP_SOURCE_CONTAINERS = {
  WEB_APP: 'WEB_APP',
  PLUGIN: 'PLUGIN',
} as const;

export const PROD_APP_DOMAIN = 'app.mesh.ai';
export const PROD_LOGIN_DOMAIN = 'login.mesh.ai';
export const DEMO_APP_DOMAIN = 'public-demo.mesh.ai';
// export const PROD_APP_DOMAIN = 'localhost';
// export const PROD_LOGIN_DOMAIN = 'localhost';

export const GOAL_VISIBILITY_OPTIONS = [
  {
    label: ' Owners, Collaborators and their Managers',
    value: 'PRIVATE',
    icon: 'PrivateViewIcon',
  },
  { label: 'Public', value: 'GLOBAL', icon: 'PublicViewIcon' },
];

export const AGENDA_STATUSES = {
  completed: 'COMPLETED',
  active: 'ACTIVE',
} as const;

export const AGENDA_TAG_LABEL_TYPES = {
  LABEL_TASK: { value: 'LABEL_TASK', displayName: 'Initiatives' },
  LABEL_GOAL: { value: 'LABEL_GOAL', displayName: 'Goals' },
  LABEL_LEVELUP: { value: 'LABEL_LEVELUP', displayName: 'Develop' },
  LABEL_FEEDBACK: { value: 'LABEL_FEEDBACK', displayName: 'Feedback' },
  LABEL_AGENDA: { value: 'LABEL_AGENDA', displayName: 'Agenda' },
};

export const TASK_EMPLOYEE_TYPES = {
  owner: 'OWNER',
  collaborator: 'COLLABORATOR',
} as const;

export const REFRESH_OBJECTIVE_TABLE_TEXT = 'Please refresh the table to see udpated data.';
export const MESH_RECOMMENDATIONS_TYPES = {
  REVIEW_PUBLISHED: 'REVIEW_PUBLISHED',
} as const;

export const COHORT_TYPES_GOALS = {
  MY_OKRs: 'MY_OKRs',
  MY_TEAM: 'MY_TEAM',
  MY_ORGANIZATION: 'MY_ORGANIZATION',
  ALL_EMPLOYEE: 'ALL_EMPLOYEE',
  CUSTOM_TYPE: 'CUSTOM_TYPE',
  POD: 'POD',
  MY_DEPARTMENT: 'MY_DEPARTMENT',
} as const;

export const OKR_TAG_TYPES = {
  OKR_TAG: 'OKR_TAG',
  SUB_OKR_TAG: 'SUB_OKR_TAG',
} as const;

export const OBJECTIVE_ACCESS_PERMISSIONS = [
  { label: 'Only Admins can create', value: 'ADMIN' },
  { label: 'Both Admins and Managers can create', value: 'ADMIN_AND_MANAGER' },
];

export const VIEWS_FILTER_KEYS = {
  BASE_COHORT: 'base_cohort',
  GRADE: 'grade',
  ROLE: 'roleId',
  FUNCTION: 'functionId',
  DEPARTMENT: 'department',
  LOCATION: 'location',
  COUNTRY: 'country', // check if this exists
  GENDER: 'gender',
  ETHNICITY: 'ethnicity',
  GOALS: 'goals',
  ONE_ON_ONE: 'oneOnOne',
  REVIEW: 'review',
  ENGAGEMENT: 'engagement',
  FEEDBACK: 'feedback',
  MANAGERS: 'managers',
  EMPLOYEES: 'employees',
  CSV_EMPLOYEES: 'csvEmployees',
  SIDEBAR_VIEW_COHORTS: 'sidebarViewCohorts',
  TALENT_PARTNER_EMAIL: 'talentPartnerEmail',
} as const;

export const VIEWS_FILTER_KEYS_DISPLAY_NAMES = {
  [VIEWS_FILTER_KEYS.BASE_COHORT]: null,
  [VIEWS_FILTER_KEYS.GRADE]: 'Grade',
  [VIEWS_FILTER_KEYS.ROLE]: 'Role',
  [VIEWS_FILTER_KEYS.FUNCTION]: 'Function',
  [VIEWS_FILTER_KEYS.DEPARTMENT]: 'Department',
  [VIEWS_FILTER_KEYS.LOCATION]: 'Location',
  [VIEWS_FILTER_KEYS.COUNTRY]: 'Country', // check if this exists
  [VIEWS_FILTER_KEYS.GENDER]: 'Sex',
  [VIEWS_FILTER_KEYS.ETHNICITY]: 'Race',
  [VIEWS_FILTER_KEYS.TALENT_PARTNER_EMAIL]: 'Talent Partner Email',
  [VIEWS_FILTER_KEYS.GOALS]: 'Goals',
  [VIEWS_FILTER_KEYS.ONE_ON_ONE]: '1:1',
  [VIEWS_FILTER_KEYS.REVIEW]: 'Review',
  [VIEWS_FILTER_KEYS.ENGAGEMENT]: 'Engagement',
  [VIEWS_FILTER_KEYS.FEEDBACK]: 'Feedback',
  [VIEWS_FILTER_KEYS.MANAGERS]: 'Manager',
  [VIEWS_FILTER_KEYS.EMPLOYEES]: 'Name',
} as const;

export const VIEWS_VISIBILITY_TYPES_LABELS = {
  PRIVATE: 'Private',
  SHARED: 'Shared',
} as const;
export const VIEWS_VISIBILITY_TYPES_VALUES = {
  PRIVATE: 'PRIVATE',
  SHARED: 'ADMIN',
} as const;

export const VIEWS_DYNAMIC_TYPES_LABELS = {
  DYNAMIC: 'Dynamic',
  STATIC: 'Static',
} as const;
export const VIEWS_DYNAMIC_TYPES_VALUES = {
  DYNAMIC: 'DYNAMIC',
  STATIC: 'STATIC',
} as const;

export const MAX_OKR_NAME_LENGTH = 500;
export const MIN_OKR_NAME_LENGTH = 1;
export const MAX_OKR_DESCRIPTION_LENGTH = 5000;
export const MIN_OKR_DESCRIPTION_LENGTH = 0;
export const MAX_OKR_METRIC_LENGTH = 25;
export const MIN_OKR_METRIC_LENGTH = 0;

export const OKR_PROGRESS_TYPES = {
  MANUAL: 'MANUAL',
  DERIVED_SUM: 'DERIVED_SUM',
  DERIVED_AVG: 'DERIVED_AVG',
  TRACKED: 'TRACKED',
} as const;

export const OKR_TYPES = {
  KEY_RESULT: 'GOAL',
  OBJECTIVE: 'OBJECTIVE',
} as const;

export const OBJECTIVE_TYPES_MAP = {
  SIMPLE: { label: 'Simple', value: 'SIMPLE' },
  COMPACT: { label: 'Compact', value: 'COMPACT' },
  DETAILED: { label: 'Detailed', value: 'DETAILED' },
  DISABLED: { label: 'Disabled', value: 'DISABLED' },
};
export const OBJECTIVE_TYPES_LIST = [
  OBJECTIVE_TYPES_MAP.SIMPLE,
  OBJECTIVE_TYPES_MAP.COMPACT,
  OBJECTIVE_TYPES_MAP.DETAILED,
  OBJECTIVE_TYPES_MAP.DISABLED,
];

export const OBJECTIVE_VISIBILITY_TYPES_MAP = {
  PUBLIC: { label: 'Public', value: 'PUBLIC' },
  PRIVATE: { label: 'Private', value: 'PRIVATE' },
};

export const OBJECTIVE_VISIBILITY_TYPES_LIST = [
  OBJECTIVE_VISIBILITY_TYPES_MAP.PUBLIC,
  OBJECTIVE_VISIBILITY_TYPES_MAP.PRIVATE,
];

export const OBJECTIVE_PROGRESS_TYPES_MAP = {
  DERIVED: { label: 'Derived', value: 'DERIVED' },
  MANUAL: { label: 'Manual', value: 'MANUAL' },
  TRACKED: { label: 'Tracked', value: 'TRACKED' },
};

export const OBJECTIVE_PROGRESS_TYPES_LIST = [
  OBJECTIVE_PROGRESS_TYPES_MAP.DERIVED,
  OBJECTIVE_PROGRESS_TYPES_MAP.MANUAL,
  OBJECTIVE_PROGRESS_TYPES_MAP.TRACKED,
];

/* Privileges enums */

export const CREATE_OBJECTIVE_PRIVILEGES = {
  CREATE_OBJECTIVE_INDIVIDUAL: 'CREATE_OBJECTIVE_INDIVIDUAL',
  CREATE_OBJECTIVE_TEAM: 'CREATE_OBJECTIVE_TEAM',
  CREATE_OBJECTIVE_DEPARTMENT: 'CREATE_OBJECTIVE_DEPARTMENT',
  CREATE_OBJECTIVE_COMPANY: 'CREATE_OBJECTIVE_COMPANY',
} as const;
export const CREATE_KEY_RESULT_PRIVILEGES = {
  CREATE_KEY_RESULT_PARENT_MANDATORY: 'CREATE_KEY_RESULT_PARENT_MANDATORY',
  CREATE_KEY_RESULT_PARENT_NON_MANDATORY: 'CREATE_KEY_RESULT_PARENT_NON_MANDATORY',
} as const;

/* Insights View and Download Privileges */
export const VIEW_DOWNLOAD_INSIGHT_PRIVILEGES = {
  VIEW_INDIVIDUAL_INSIGHTS: 'VIEW_INDIVIDUAL_INSIGHTS',
  DOWNLOAD_INDIVIDUAL_INSIGHTS: 'DOWNLOAD_INDIVIDUAL_INSIGHTS',
  VIEW_OBJECTIVE_LEVEL_INSIGHTS: 'VIEW_OBJECTIVE_LEVEL_INSIGHTS',
  DOWNLOAD_OBJECTIVE_LEVEL_INSIGHTS: 'DOWNLOAD_OBJECTIVE_LEVEL_INSIGHTS',
} as const;

/* Bulk Upload Privileges */
export const BULK_UPLOAD_PRIVILEGES = {
  BULK_UPLOAD_OBJECTIVES: 'BULK_UPLOAD_OBJECTIVES',
  BULK_UPLOAD_KEY_RESULTS: 'BULK_UPLOAD_KEY_RESULTS',
  BULK_UPLOAD_INITIATIVES: 'BULK_UPLOAD_INITIATIVES',
} as const;

export const KR_VISIBILITY_TYPES_MAP = OBJECTIVE_VISIBILITY_TYPES_MAP;
export const KR_VISIBILITY_TYPES_LIST = OBJECTIVE_VISIBILITY_TYPES_LIST;
export const KR_PROGRESS_TYPES_MAP = OBJECTIVE_PROGRESS_TYPES_MAP;
export const KR_PROGRESS_TYPES_LIST = OBJECTIVE_PROGRESS_TYPES_LIST;

export const TOAST_STATUSES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
} as const;

export const GRID_COLORS_MAP = {
  blue: { text: 'functional.1565BC', bg: 'feedback.D6E7F9' },
  grey: {
    text: 'neutral.666666',
    bg: 'neutral.EBEFF3',
  },
  green: {
    text: 'functional.1A5724',
    bg: 'feedback.BFE8C6',
  },

  orange: {
    text: 'functional.AF5C20',
    bg: 'functional.FFE7C5',
  },

  red: {
    text: 'functional.C62C02',
    bg: 'feedback.FFE1D9',
  },
};

export const GRID_COLORS_LABELS = ['Green', 'Red', 'Blue', 'Orange', 'Grey'];

export const GRID_COLORS_KEYS = ['green', 'red', 'blue', 'orange', 'grey'];

export const DEMO_DEFAULT_GRID_COLORS = [
  ['grey', 'green', 'blue', 'red'],
  ['red', 'red', 'green', 'blue'],
  ['red', 'orange', 'orange', 'grey'],
  ['green', 'red', 'orange', 'grey'],
];

export const DEMO_DEFAULT_PERFORMANCE_LABELS = [
  ['Potential Gem', 'High Potential', 'High Potential', 'Star'],
  ['Potential Gem', 'Potential Gem', 'High Potential', 'Star'],
  ['Inconsistent Player', 'Core Player', 'Potential Gem', 'High Potential'],
  ['Risk', 'Average', 'Average', 'Solid Performer'],
];
export const CREATE_EDIT_DELETE_CUSTOM_TAGS = 'CREATE_EDIT_DELETE_CUSTOM_TAGS';
export const VIEW_ASSIGN_CUSTOM_TAGS = 'VIEW_ASSIGN_CUSTOM_TAGS';
export const MODULE_KEYS_USER_PREFERENCE_PERSISTENCE = MODULE_KEYS;

export const INPROGRESS_STATUS_COLOR_MAP = {
  ON_TRACK: {
    barColor: '#B1E39F',
    textColor: themeColors.functional['44812F'],
    tagColor: themeColors.feedback.EFFFEA,
  },
  OFF_TRACK: {
    barColor: '#FCC88B',
    textColor: themeColors.functional['8C6A00'],
    tagColor: themeColors.feedback.FFF6D3,
  },
  AT_RISK: {
    barColor: '#FD9B9B',
    textColor: themeColors.functional.C62C02,
    tagColor: themeColors.feedback.FFF1E4,
  },
  // for case where closed sub status is undefined
  CLOSED: {
    barColor: themeColors.neutral.CCCCCC,
    textColor: themeColors.secondary['666666'],
    tagColor: themeColors.neutral.CCCCCC,
  },
};

export const CLOSED_STATUS_COLOR_MAP = {
  ACHIEVED: {
    textColor: themeColors.functional['1A5724'],
    tagColor: themeColors.feedback.DBF2D3,
  },
  NOT_ACHIEVED: {
    textColor: themeColors.functional.A12402,
    tagColor: themeColors.feedback.FFE1D9,
  },
  DROPPED: {
    textColor: themeColors.secondary['666666'],
    tagColor: themeColors.neutral.CCCCCC,
  },
};

export const getGoalStatusColorObject = (status, goalClosureStatus) =>
  status === 'CLOSED' && goalClosureStatus
    ? CLOSED_STATUS_COLOR_MAP[goalClosureStatus]
    : INPROGRESS_STATUS_COLOR_MAP[status];

export const COLOR_MAP_FULL = {
  ...INPROGRESS_STATUS_COLOR_MAP,
  CLOSED_ACHIEVED: { ...CLOSED_STATUS_COLOR_MAP.ACHIEVED },
  CLOSED_NOT_ACHIEVED: { ...CLOSED_STATUS_COLOR_MAP.NOT_ACHIEVED },
  CLOSED_DROPPED: { ...CLOSED_STATUS_COLOR_MAP.DROPPED },
};

export const getGoalStatusColor = (status, goalClosureStatus) => {
  const progressColors = getGoalStatusColorObject(status, goalClosureStatus);
  return progressColors?.textColor ?? palette.primary.main;
};

export const GOAL_PROGRESSES_PARTIAL_LABEL = {
  ON_TRACK: 'On-track',
  OFF_TRACK: 'Off-track',
  AT_RISK: 'At-risk',
  CLOSED: 'Closed',
} as const;

export const GOAL_PROGRESSES_FULL_LABEL = {
  ON_TRACK: 'On-track',
  OFF_TRACK: 'Off-track',
  AT_RISK: 'At-risk',
  CLOSED: 'Closed',
  CLOSED_ACHIEVED: 'Closed & achieved',
  CLOSED_DROPPED: 'Closed & dropped',
  CLOSED_NOT_ACHIEVED: 'Closed & not achieved',
} as const;

export const GOAL_PROGRESS_STATUS_OBJECTS = {
  onTrack: {
    value: 'ON_TRACK',
    label: 'On-Track',
    color: COLOR_MAP_FULL.ON_TRACK.textColor,
    textColor: COLOR_MAP_FULL.ON_TRACK.textColor,
    tagColor: COLOR_MAP_FULL.ON_TRACK.tagColor,
    progressLevel: 'ON_TRACK',
    order: 0,
  },
  offTrack: {
    value: 'OFF_TRACK',
    label: 'Off-Track',
    color: COLOR_MAP_FULL.OFF_TRACK.textColor,
    textColor: COLOR_MAP_FULL.OFF_TRACK.textColor,
    tagColor: COLOR_MAP_FULL.OFF_TRACK.tagColor,
    progressLevel: 'OFF_TRACK',
    order: 1,
  },
  atRisk: {
    value: 'AT_RISK',
    label: 'At-Risk',
    color: COLOR_MAP_FULL.AT_RISK.textColor,
    textColor: COLOR_MAP_FULL.AT_RISK.textColor,
    tagColor: COLOR_MAP_FULL.AT_RISK.tagColor,
    progressLevel: 'AT_RISK',
    order: 2,
  },
  closed: {
    value: 'CLOSED',
    label: 'Closed',
    color: COLOR_MAP_FULL.CLOSED.textColor,
    textColor: COLOR_MAP_FULL.CLOSED.textColor,
    tagColor: COLOR_MAP_FULL.CLOSED.tagColor,
    progressLevel: 'CLOSED',
    order: 3,
  },
  closedAchieved: {
    value: 'ACHIEVED',
    label: 'Closed - Achieved',
    shortLabel: 'Closed(Ach)',
    color: COLOR_MAP_FULL.CLOSED_ACHIEVED.textColor,
    textColor: COLOR_MAP_FULL.CLOSED_ACHIEVED.textColor,
    tagColor: COLOR_MAP_FULL.CLOSED_ACHIEVED.tagColor,
    progressLevel: 'CLOSED',
    order: undefined,
  },
  closedNonAchieved: {
    value: 'NOT_ACHIEVED',
    label: 'Closed - Not Achieved',
    shortLabel: 'Closed(NAch)',
    color: COLOR_MAP_FULL.CLOSED_NOT_ACHIEVED.textColor,
    textColor: COLOR_MAP_FULL.CLOSED_NOT_ACHIEVED.textColor,
    tagColor: COLOR_MAP_FULL.CLOSED_NOT_ACHIEVED.tagColor,
    progressLevel: 'CLOSED',
    order: undefined,
  },
  closedDropped: {
    value: 'DROPPED',
    label: 'Closed - Dropped',
    shortLabel: 'Closed(Drop)',
    color: COLOR_MAP_FULL.CLOSED_DROPPED.textColor,
    textColor: COLOR_MAP_FULL.CLOSED_DROPPED.textColor,
    tagColor: COLOR_MAP_FULL.CLOSED_DROPPED.tagColor,
    progressLevel: 'CLOSED',
    order: undefined,
  },
};
export const GOAL_IN_PROGRESS_OPTIONS = [
  GOAL_PROGRESS_STATUS_OBJECTS.onTrack,
  GOAL_PROGRESS_STATUS_OBJECTS.offTrack,
  GOAL_PROGRESS_STATUS_OBJECTS.atRisk,
];

export const GOAL_CLOSED_OPTIONS = [
  // 'ACHIEVED','NOT_ACHIEVED','DROPPED'
  GOAL_PROGRESS_STATUS_OBJECTS.closedAchieved,
  GOAL_PROGRESS_STATUS_OBJECTS.closedNonAchieved,
  GOAL_PROGRESS_STATUS_OBJECTS.closedDropped,
];

export const GOAL_PROGRESS_OPTIONS = [
  GOAL_PROGRESS_STATUS_OBJECTS.onTrack,
  GOAL_PROGRESS_STATUS_OBJECTS.offTrack,
  GOAL_PROGRESS_STATUS_OBJECTS.atRisk,
  GOAL_PROGRESS_STATUS_OBJECTS.closed,
  ...GOAL_CLOSED_OPTIONS,
];

export const GOAL_PROGRESS_OPTIONS_MAP = GOAL_PROGRESS_OPTIONS.reduce(
  (prevValue, currentValue) => ({
    // biome-ignore lint/performance/noAccumulatingSpread: Small fixed-size array, performance impact negligible
    ...prevValue,
    [currentValue.value]: {
      label: currentValue.label,
      color: currentValue.color,
      textColor: currentValue.textColor,
      tagColor: currentValue.tagColor,
      order: currentValue.order,
    },
  }),
  {}
);
export const FINCH_CLIENT_ID = '283a0567-b748-4bf8-9e83-607ba9683c36';
export const FINCH_CLIENT_ID_PROD = '9009206c-008c-40e6-9862-293ca001153d';

export const COHORT_TYPES_ONE_ON_ONE = {
  MY_ONE_ON_ONE: 'MY_ONE_ON_ONE',
  MY_COMPANY: 'ALL_EMPLOYEE',
  MY_ORGANIZATION_TREE: 'MY_ORGANIZATION',
} as const;
export const COHORT_TYPES_ENGAGEMENT = {
  MY_COMPANY: 'ALL_EMPLOYEE',
  MY_ORGANIZATION_TREE: 'MY_ORGANIZATION',
  MY_TEAM: 'MY_TEAM',
} as const;

export const COHORT_TYPES_REVIEW = {
  MY_REVIEWS: 'MY_REVIEWS',
  MY_COMPANY: 'ALL_EMPLOYEE',
  MY_ORGANIZATION_TREE: 'MY_ORGANIZATION',
  MY_TEAM: 'MY_TEAM',
} as const;

export const VISIER_NAME_MAPPING = {
  ANALYZING_GOAL_ACHIEVEMENT: 'Analyzing Goal Achievement',
  ANALYZING_PERFORMANCE: 'Analyzing Performance',
  ANALYZING_ENGAGEMENT: 'Analyzing Engagement',
  ANALYZING_MANAGER_1_1S: 'Analyzing Manager 1:1s',
  ANALYZING_COMPETENCIES: 'Analyzing Competencies',
} as const;

export const COMPANY_VISIER_ANALYSIS = [
  {
    id: '7349cda4-70c2-41cc-b359-024e1da3a0ee',
    name: VISIER_NAME_MAPPING.ANALYZING_GOAL_ACHIEVEMENT,
    url: 'https://prod-ap00.app.visier.com/hr/prod/appcontainer#/analytics/myanalyses/7349cda4-70c2-41cc-b359-024e1da3a0ee',
  },
  {
    id: 'cb01b3fb-b5f5-40f2-98d2-6d4c9ae61607',
    name: VISIER_NAME_MAPPING.ANALYZING_PERFORMANCE,
    url: 'https://prod-ap00.app.visier.com/hr/prod/appcontainer#/analytics/myanalyses/cb01b3fb-b5f5-40f2-98d2-6d4c9ae61607',
  },
  {
    id: '331305bd-726c-483c-9193-dd9c895b9e34',
    name: VISIER_NAME_MAPPING.ANALYZING_ENGAGEMENT,
    url: 'https://prod-ap00.app.visier.com/hr/prod/appcontainer#/analytics/myanalyses/331305bd-726c-483c-9193-dd9c895b9e34',
  },
  {
    id: '833e877a-e79d-4ad0-b958-21ce3619c8cb',
    name: VISIER_NAME_MAPPING.ANALYZING_MANAGER_1_1S,
    url: 'https://prod-ap00.app.visier.com/hr/prod/appcontainer#/analytics/myanalyses/833e877a-e79d-4ad0-b958-21ce3619c8cb',
  },
  {
    id: '6c502d38-1af3-4ec9-817f-385af8e1c7e9',
    name: VISIER_NAME_MAPPING.ANALYZING_COMPETENCIES,
    url: 'https://prod-ap00.app.visier.com/hr/prod/appcontainer#/analytics/myanalyses/6c502d38-1af3-4ec9-817f-385af8e1c7e9',
  },
];

export const TEAM_DIRECT_VISIER_ANALYSIS = [
  {
    id: '1a33480d-0784-48e8-b174-c766cfcc5fab',
    name: VISIER_NAME_MAPPING.ANALYZING_GOAL_ACHIEVEMENT,
    url: 'https://prod-ap00.app.visier.com/hr/prod/appcontainer#/analytics/myanalyses/7349cda4-70c2-41cc-b359-024e1da3a0ee?contextFilters={"filters":[{"concept":{"conceptId":"isMyDirectReports","subjectId":"Employee"}}]}',
  },
  {
    id: 'ba3cbf84-f1bd-4e29-a09f-4c653767f434',
    name: VISIER_NAME_MAPPING.ANALYZING_PERFORMANCE,
    url: 'https://prod-ap00.app.visier.com/hr/prod/appcontainer#/analytics/myanalyses/cb01b3fb-b5f5-40f2-98d2-6d4c9ae61607?contextFilters={"filters":[{"concept":{"conceptId":"isMyDirectReports","subjectId":"Employee"}}]}',
  },
  {
    id: '83d86276-20a2-4e9f-b020-2de9447c9496',
    name: VISIER_NAME_MAPPING.ANALYZING_MANAGER_1_1S,
    url: 'https://prod-ap00.app.visier.com/hr/prod/appcontainer#/analytics/myanalyses/833e877a-e79d-4ad0-b958-21ce3619c8cb?contextFilters={"filters":[{"concept":{"conceptId":"isMyDirectReports","subjectId":"Employee"}}]}',
  },
  {
    id: '14ffae65-f4da-4727-99d3-21dbecc70aa7',
    name: VISIER_NAME_MAPPING.ANALYZING_COMPETENCIES,
    url: 'https://prod-ap00.app.visier.com/hr/prod/appcontainer#/analytics/myanalyses/6c502d38-1af3-4ec9-817f-385af8e1c7e9?contextFilters={"filters":[{"concept":{"conceptId":"isMyDirectReports","subjectId":"Employee"}}]}',
  },
];

export const TEAM_SKIP_VISIER_ANALYSIS = [
  {
    id: 'd223711b-ca26-4857-b4de-6cd3fb422ba3',
    name: VISIER_NAME_MAPPING.ANALYZING_GOAL_ACHIEVEMENT,
    url: 'https://prod-ap00.app.visier.com/hr/prod/appcontainer#/analytics/myanalyses/7349cda4-70c2-41cc-b359-024e1da3a0ee?contextFilters={"filters":[{"concept":{"conceptId":"isMyReports","subjectId":"Employee"}}]}',
  },
  {
    id: '9a0b8cd5-7a27-4e25-b8ba-06c4a9d539e1',
    name: VISIER_NAME_MAPPING.ANALYZING_PERFORMANCE,
    url: 'https://prod-ap00.app.visier.com/hr/prod/appcontainer#/analytics/myanalyses/cb01b3fb-b5f5-40f2-98d2-6d4c9ae61607?contextFilters={"filters":[{"concept":{"conceptId":"isMyReports","subjectId":"Employee"}}]}',
  },
  {
    id: '53c964e8-d712-47a6-afeb-a04bdd08a550',
    name: VISIER_NAME_MAPPING.ANALYZING_MANAGER_1_1S,
    url: 'https://prod-ap00.app.visier.com/hr/prod/appcontainer#/analytics/myanalyses/833e877a-e79d-4ad0-b958-21ce3619c8cb?contextFilters={"filters":[{"concept":{"conceptId":"isMyReports","subjectId":"Employee"}}]}',
  },
  {
    id: 'b7e017e5-f277-4d38-9950-61752f8ecbb9',
    name: VISIER_NAME_MAPPING.ANALYZING_COMPETENCIES,
    url: 'https://prod-ap00.app.visier.com/hr/prod/appcontainer#/analytics/myanalyses/6c502d38-1af3-4ec9-817f-385af8e1c7e9?contextFilters={"filters":[{"concept":{"conceptId":"isMyReports","subjectId":"Employee"}}]}',
  },
];

export const VISIER_DATA_EXPORT_ROOMS = {
  reviews:
    'https://prod-ap00.app.visier.com/hr/prod/appcontainer#/analytics/dataconnector/068e878f-6db5-4b91-b1d8-7a46f0c6a6f1?mode=stand-alone',
  goals: 'https://prod-ap00.app.visier.com/hr/prod/appcontainer#/analytics/dataconnector',
  oneOnOne:
    'https://prod-ap00.app.visier.com/hr/prod/appcontainer#/analytics/dataconnector/01d62375-9f08-4b7b-9400-7b5dccacc7ab?mode=stand-alone',
  reports: 'https://prod-ap00.app.visier.com/hr/prod/appcontainer#/analytics/dataconnector',
  outcomesDashboard:
    'https://prod-ap00.app.visier.com/hr/prod/appcontainer#/analytics/myanalyses/dfeb71a4-fc0d-4ac2-a360-b88577743ebb',
} as const;

export const COHORT_TYPES_FEEDBACK = {
  MY_FEEDBACK: 'MY_FEEDBACK',
  MY_COMPANY: 'ALL_EMPLOYEE',
  MY_ORGANIZATION_TREE: 'MY_ORGANIZATION',
  MY_TEAM: 'MY_TEAM',
} as const;

export const UPLOAD_GOAL_HELP_URL = 'https://help.mesh.ai/hc/en-us/articles/20545366607645-Creating-goals-via-upload';

export const getPeerTypeTagNameMap = () =>
  ({
    JUNIORS: 'Direct Team',
    SENIORS: 'Leader',
    PRIMARY_TEAM: 'Team Peer',
    OUTSIDE_TEAM: 'Internal Client',
    ALL_ORG: 'Peer',
    DLM: getAdditionalReviewerTerm(),
  }) as const;

export const PARTICIPANTS_SELECTION_MODULES = {
  REVIEW: 'REVIEW',
  ENGAGEMENT: 'ENGAGEMENT',
  COMPENSATION: 'COMPENSATION',
} as const;

export const DISCIPLINARY_ACTIONS_GRAPH_TYPES = {
  DISCIPLINARY_ACTION_NOTES: 'DISCIPLINARY_ACTION_NOTES',
  DISCIPLINARY_ACTION_NOTES_STAGE_BREAKUP: 'DISCIPLINARY_ACTION_NOTES_STAGE_BREAKUP',
} as const;
